/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { Trace, TypedEventEmitter } from "@fluid-internal/client-utils";
import { AttachState, } from "@fluidframework/container-definitions";
import { LoaderHeader, } from "@fluidframework/container-definitions/internal";
import { assert, Deferred, LazyPromise, PromiseCache, delay, } from "@fluidframework/core-utils/internal";
import { SummaryType, } from "@fluidframework/driver-definitions";
import { DriverHeader, FetchSource, MessageType, } from "@fluidframework/driver-definitions/internal";
import { readAndParse } from "@fluidframework/driver-utils/internal";
import { FlushMode, FlushModeExperimental, channelsTreeName, gcTreeKey, } from "@fluidframework/runtime-definitions/internal";
import { GCDataBuilder, RequestParser, TelemetryContext, addBlobToSummary, addSummarizeResultToSummary, calculateStats, create404Response, exceptionToResponse, responseToException, seqFromTree, } from "@fluidframework/runtime-utils/internal";
import { DataCorruptionError, DataProcessingError, GenericError, LoggingError, PerformanceEvent, 
// eslint-disable-next-line import/no-deprecated
TaggedLoggerAdapter, UsageError, createChildLogger, createChildMonitoringContext, createSampledLogger, loggerToMonitoringContext, raiseConnectedEvent, wrapError, tagCodeArtifacts, } from "@fluidframework/telemetry-utils/internal";
import { v4 as uuid } from "uuid";
import { BindBatchTracker } from "./batchTracker.js";
import { BlobManager, blobManagerBasePath, blobsTreeName, isBlobPath, loadBlobManagerLoadInfo, } from "./blobManager/index.js";
import { ChannelCollection, getSummaryForDatastores, wrapContext, } from "./channelCollection.js";
import { ReportOpPerfTelemetry } from "./connectionTelemetry.js";
import { ContainerFluidHandleContext } from "./containerHandleContext.js";
import { channelToDataStore } from "./dataStore.js";
import { FluidDataStoreRegistry } from "./dataStoreRegistry.js";
import { DeltaManagerPendingOpsProxy, DeltaManagerSummarizerProxy, } from "./deltaManagerProxies.js";
import { GCNodeType, GarbageCollector, gcGenerationOptionName, } from "./gc/index.js";
import { ContainerMessageType, } from "./messageTypes.js";
import { ensureContentsDeserialized, OpCompressor, OpDecompressor, OpGroupingManager, OpSplitter, Outbox, RemoteMessageProcessor, } from "./opLifecycle/index.js";
import { pkgVersion } from "./packageVersion.js";
import { PendingStateManager, } from "./pendingStateManager.js";
import { ScheduleManager } from "./scheduleManager.js";
import { DocumentsSchemaController, OrderedClientCollection, OrderedClientElection, RetriableSummaryError, RunWhileConnectedCoordinator, Summarizer, SummarizerClientElection, SummaryCollection, SummaryManager, aliasBlobName, chunksBlobName, createRootSummarizerNodeWithGC, electedSummarizerBlobName, extractSummaryMetadataMessage, idCompressorBlobName, metadataBlobName, rootHasIsolatedChannels, summarizerClientType, wrapSummaryInChannelsTree, } from "./summary/index.js";
import { Throttler, formExponentialFn } from "./throttler.js";
/**
 * Utility to implement compat behaviors given an unknown message type
 * The parameters are typed to support compile-time enforcement of handling all known types/behaviors
 *
 * @param _unknownContainerRuntimeMessageType - Typed as something unexpected, to ensure all known types have been
 * handled before calling this function (e.g. in a switch statement).
 * @param compatBehavior - Typed redundantly with CompatModeBehavior to ensure handling is added when updating that type
 */
function compatBehaviorAllowsMessageType(_unknownContainerRuntimeMessageType, compatBehavior) {
    // undefined defaults to same behavior as "FailToProcess"
    return compatBehavior === "Ignore";
}
/**
 * @legacy
 * @alpha
 */
export const DefaultSummaryConfiguration = {
    state: "enabled",
    minIdleTime: 0,
    maxIdleTime: 30 * 1000, // 30 secs.
    maxTime: 60 * 1000, // 1 min.
    maxOps: 100, // Summarize if 100 weighted ops received since last snapshot.
    minOpsForLastSummaryAttempt: 10,
    maxAckWaitTime: 3 * 60 * 1000, // 3 mins.
    maxOpsSinceLastSummary: 7000,
    initialSummarizerDelayMs: 5 * 1000, // 5 secs.
    nonRuntimeOpWeight: 0.1,
    runtimeOpWeight: 1.0,
    nonRuntimeHeuristicThreshold: 20,
};
/**
 * Error responses when requesting a deleted object will have this header set to true
 * @legacy
 * @alpha
 */
export const DeletedResponseHeaderKey = "wasDeleted";
/**
 * Tombstone error responses will have this header set to true
 * @legacy
 * @alpha
 */
export const TombstoneResponseHeaderKey = "isTombstoned";
/**
 * Inactive error responses will have this header set to true
 * @legacy
 * @alpha
 *
 * @deprecated this header is deprecated and will be removed in the future. The functionality corresponding
 * to this was experimental and is no longer supported.
 */
export const InactiveResponseHeaderKey = "isInactive";
/** Default values for Runtime Headers */
export const defaultRuntimeHeaderData = {
    wait: true,
    viaHandle: false,
    allowTombstone: false,
};
/**
 * Available compression algorithms for op compression.
 * @legacy
 * @alpha
 */
export var CompressionAlgorithms;
(function (CompressionAlgorithms) {
    CompressionAlgorithms["lz4"] = "lz4";
})(CompressionAlgorithms || (CompressionAlgorithms = {}));
/**
 * @legacy
 * @alpha
 */
export const disabledCompressionConfig = {
    minimumBatchSizeInBytes: Infinity,
    compressionAlgorithm: CompressionAlgorithms.lz4,
};
const maxConsecutiveReconnectsKey = "Fluid.ContainerRuntime.MaxConsecutiveReconnects";
const defaultFlushMode = FlushMode.TurnBased;
// The actual limit is 1Mb (socket.io and Kafka limits)
// We can't estimate it fully, as we
// - do not know what properties relay service will add
// - we do not stringify final op, thus we do not know how much escaping will be added.
const defaultMaxBatchSizeInBytes = 700 * 1024;
const defaultCompressionConfig = {
    // Batches with content size exceeding this value will be compressed
    minimumBatchSizeInBytes: 614400,
    compressionAlgorithm: CompressionAlgorithms.lz4,
};
const defaultChunkSizeInBytes = 204800;
/** The default time to wait for pending ops to be processed during summarization */
export const defaultPendingOpsWaitTimeoutMs = 1000;
/** The default time to delay a summarization retry attempt when there are pending ops */
export const defaultPendingOpsRetryDelayMs = 1000;
/**
 * Instead of refreshing from latest because we do not have 100% confidence in the state
 * of the current system, we should close the summarizer and let it recover.
 * This delay's goal is to prevent tight restart loops
 */
const defaultCloseSummarizerDelayMs = 5000; // 5 seconds
/**
 * Checks whether a message.type is one of the values in ContainerMessageType
 * @deprecated please use version in driver-utils
 * @internal
 */
export function isRuntimeMessage(message) {
    return Object.values(ContainerMessageType).includes(message.type);
}
/**
 * Legacy ID for the built-in AgentScheduler.  To minimize disruption while removing it, retaining this as a
 * special-case for document dirty state.  Ultimately we should have no special-cases from the
 * ContainerRuntime's perspective.
 * @internal
 */
export const agentSchedulerId = "_scheduler";
// safely check navigator and get the hardware spec value
export function getDeviceSpec() {
    try {
        if (typeof navigator === "object" && navigator !== null) {
            return {
                deviceMemory: navigator.deviceMemory,
                hardwareConcurrency: navigator.hardwareConcurrency,
            };
        }
    }
    catch { }
    return {};
}
/**
 * Older loader doesn't have a submitBatchFn member, this is the older way of submitting a batch.
 * Rather than exposing the submitFn (now deprecated) and IDeltaManager (dangerous to hand out) to the Outbox,
 * we can provide a partially-applied function to keep those items private to the ContainerRuntime.
 */
export const makeLegacySendBatchFn = (submitFn, deltaManager) => (batch) => {
    // Default to negative one to match Container.submitBatch behavior
    let clientSequenceNumber = -1;
    for (const message of batch.messages) {
        clientSequenceNumber = submitFn(MessageType.Operation, 
        // For back-compat (submitFn only works on deserialized content)
        message.contents === undefined ? undefined : JSON.parse(message.contents), true, // batch
        message.metadata);
    }
    deltaManager.flush();
    return clientSequenceNumber;
};
const summarizerRequestUrl = "_summarizer";
/**
 * Create and retrieve the summmarizer
 */
async function createSummarizer(loader, url) {
    const request = {
        headers: {
            [LoaderHeader.cache]: false,
            [LoaderHeader.clientDetails]: {
                capabilities: { interactive: false },
                type: summarizerClientType,
            },
            [DriverHeader.summarizingClient]: true,
            [LoaderHeader.reconnect]: false,
        },
        url,
    };
    const resolvedContainer = await loader.resolve(request);
    let fluidObject;
    // Older containers may not have the "getEntryPoint" API
    // ! This check will need to stay until LTS of loader moves past 2.0.0-internal.7.0.0
    if (resolvedContainer.getEntryPoint !== undefined) {
        fluidObject = await resolvedContainer.getEntryPoint();
    }
    else {
        const response = await resolvedContainer.request({
            url: `/${summarizerRequestUrl}`,
        });
        if (response.status !== 200 || response.mimeType !== "fluid/object") {
            throw responseToException(response, request);
        }
        fluidObject = response.value;
    }
    if (fluidObject?.ISummarizer === undefined) {
        throw new UsageError("Fluid object does not implement ISummarizer");
    }
    return fluidObject.ISummarizer;
}
/**
 * Extract last message from the snapshot metadata.
 * Uses legacy property if not using explicit schema control, otherwise uses the new property.
 * This allows new runtime to make documents not openable for old runtimes, one explicit document schema control is enabled.
 * Please see addMetadataToSummary() as well
 */
function lastMessageFromMetadata(metadata) {
    return metadata?.documentSchema?.runtime?.explicitSchemaControl
        ? metadata?.lastMessage
        : metadata?.message;
}
/**
 * There is some ancient back-compat code that we'd like to instrument
 * to understand if/when it is hit.
 * We only want to log this once, to avoid spamming telemetry if we are wrong and these cases are hit commonly.
 */
let getSingleUseLegacyLogCallback = (logger, type) => {
    // We only want to log this once per ContainerRuntime instance, to avoid spamming telemetry.
    getSingleUseLegacyLogCallback = () => () => { };
    return (codePath) => {
        logger.sendTelemetryEvent({
            eventName: "LegacyMessageFormat",
            details: { codePath, type },
        });
    };
};
/**
 * Represents the runtime of the container. Contains helper functions/state of the container.
 * It will define the store level mappings.
 * @legacy
 * @alpha
 */
export class ContainerRuntime extends TypedEventEmitter {
    /**
     * Load the stores from a snapshot and returns the runtime.
     * @param params - An object housing the runtime properties:
     * - context - Context of the container.
     * - registryEntries - Mapping from data store types to their corresponding factories.
     * - existing - Pass 'true' if loading from an existing snapshot.
     * - requestHandler - (optional) Request handler for the request() method of the container runtime.
     * Only relevant for back-compat while we remove the request() method and move fully to entryPoint as the main pattern.
     * - runtimeOptions - Additional options to be passed to the runtime
     * - containerScope - runtime services provided with context
     * - containerRuntimeCtor - Constructor to use to create the ContainerRuntime instance.
     * This allows mixin classes to leverage this method to define their own async initializer.
     * - provideEntryPoint - Promise that resolves to an object which will act as entryPoint for the Container.
     * This object should provide all the functionality that the Container is expected to provide to the loader layer.
     */
    static async loadRuntime(params) {
        const { context, registryEntries, existing, requestHandler, provideEntryPoint, runtimeOptions = {}, containerScope = {}, containerRuntimeCtor = ContainerRuntime, } = params;
        // If taggedLogger exists, use it. Otherwise, wrap the vanilla logger:
        // back-compat: Remove the TaggedLoggerAdapter fallback once all the host are using loader > 0.45
        const backCompatContext = context;
        const passLogger = backCompatContext.taggedLogger ??
            // eslint-disable-next-line import/no-deprecated
            new TaggedLoggerAdapter(backCompatContext.logger);
        const logger = createChildLogger({
            logger: passLogger,
            properties: {
                all: {
                    runtimeVersion: pkgVersion,
                },
            },
        });
        const mc = loggerToMonitoringContext(logger);
        const { summaryOptions = {}, gcOptions = {}, loadSequenceNumberVerification = "close", flushMode = defaultFlushMode, compressionOptions = defaultCompressionConfig, maxBatchSizeInBytes = defaultMaxBatchSizeInBytes, enableRuntimeIdCompressor, chunkSizeInBytes = defaultChunkSizeInBytes, enableGroupedBatching = true, explicitSchemaControl = false, } = runtimeOptions;
        const registry = new FluidDataStoreRegistry(registryEntries);
        const tryFetchBlob = async (blobName) => {
            const blobId = context.baseSnapshot?.blobs[blobName];
            if (context.baseSnapshot && blobId) {
                // IContainerContext storage api return type still has undefined in 0.39 package version.
                // So once we release 0.40 container-defn package we can remove this check.
                assert(context.storage !== undefined, 0x1f5 /* "Attached state should have storage" */);
                return readAndParse(context.storage, blobId);
            }
        };
        const [chunks, metadata, electedSummarizerData, aliases, serializedIdCompressor] = await Promise.all([
            tryFetchBlob(chunksBlobName),
            tryFetchBlob(metadataBlobName),
            tryFetchBlob(electedSummarizerBlobName),
            tryFetchBlob(aliasBlobName),
            tryFetchBlob(idCompressorBlobName),
        ]);
        // read snapshot blobs needed for BlobManager to load
        const blobManagerSnapshot = await loadBlobManagerLoadInfo(context);
        const messageAtLastSummary = lastMessageFromMetadata(metadata);
        // Verify summary runtime sequence number matches protocol sequence number.
        const runtimeSequenceNumber = messageAtLastSummary?.sequenceNumber;
        const protocolSequenceNumber = context.deltaManager.initialSequenceNumber;
        // When we load with pending state, we reuse an old snapshot so we don't expect these numbers to match
        if (!context.pendingLocalState && runtimeSequenceNumber !== undefined) {
            // Unless bypass is explicitly set, then take action when sequence numbers mismatch.
            if (loadSequenceNumberVerification !== "bypass" &&
                runtimeSequenceNumber !== protocolSequenceNumber) {
                // Message to OCEs:
                // You can hit this error with runtimeSequenceNumber === -1 in < 2.0 RC3 builds.
                // This would indicate that explicit schema control is enabled in current (2.0 RC3+) builds and it
                // results in addMetadataToSummary() creating a poison pill for older runtimes in the form of a -1 sequence number.
                // Older runtimes do not understand new schema, and thus could corrupt document if they proceed, thus we are using
                // this poison pill to prevent them from proceeding.
                // "Load from summary, runtime metadata sequenceNumber !== initialSequenceNumber"
                const error = new DataCorruptionError(
                // pre-0.58 error message: SummaryMetadataMismatch
                "Summary metadata mismatch", { runtimeVersion: pkgVersion, runtimeSequenceNumber, protocolSequenceNumber });
                if (loadSequenceNumberVerification === "log") {
                    logger.sendErrorEvent({ eventName: "SequenceNumberMismatch" }, error);
                }
                else {
                    context.closeFn(error);
                }
            }
        }
        let desiredIdCompressorMode;
        switch (mc.config.getBoolean("Fluid.ContainerRuntime.IdCompressorEnabled")) {
            case true:
                desiredIdCompressorMode = "on";
                break;
            case false:
                desiredIdCompressorMode = undefined;
                break;
            default:
                desiredIdCompressorMode = enableRuntimeIdCompressor;
                break;
        }
        // Enabling the IdCompressor is a one-way operation and we only want to
        // allow new containers to turn it on.
        let idCompressorMode;
        if (existing) {
            // This setting has to be sticky for correctness:
            // 1) if compressior is OFF, it can't be enabled, as already running clients (in given document session) do not know
            //    how to process compressor ops
            // 2) if it's ON, then all sessions should load compressor right away
            // 3) Same logic applies for "delayed" mode
            // Maybe in the future we will need to enabled (and figure how to do it safely) "delayed" -> "on" change.
            // We could do "off" -> "on" transition too, if all clients start loading compressor (but not using it initially) and
            // do so for a while - this will allow clients to eventually disregard "off" setting (when it's safe so) and start
            // using compressor in future sessions.
            // Everyting is possible, but it needs to be designed and executed carefully, when such need arises.
            idCompressorMode = metadata?.documentSchema?.runtime
                ?.idCompressorMode;
            // This is the only exception to the rule above - we have proper plumbing to load ID compressor on schema change
            // event. It is loaded async (relative to op processing), so this conversion is only safe for off -> delayed conversion!
            // Clients do not expect ID compressor ops unless ID compressor is On for them, and that could be achieved only through
            // explicit schema change, i.e. only if explicitSchemaControl is on.
            // Note: it would be better if we throw on combination of options (explicitSchemaControl = off, desiredIdCompressorMode === "delayed")
            // that is not supported. But our service tests are oblivious to these problems and throwing here will cause a ton of failures
            // We ignored incompatible ID compressor changes from the start (they were sticky), so that's not a new problem being introduced...
            if (idCompressorMode === undefined &&
                desiredIdCompressorMode === "delayed" &&
                explicitSchemaControl) {
                idCompressorMode = desiredIdCompressorMode;
            }
        }
        else {
            idCompressorMode = desiredIdCompressorMode;
        }
        const createIdCompressorFn = async () => {
            const { createIdCompressor, deserializeIdCompressor, createSessionId } = await import("@fluidframework/id-compressor/internal");
            /**
             * Because the IdCompressor emits so much telemetry, this function is used to sample
             * approximately 5% of all clients. Only the given percentage of sessions will emit telemetry.
             */
            const idCompressorEventSampler = (() => {
                const isIdCompressorTelemetryEnabled = Math.random() < 0.05;
                return {
                    sample: () => {
                        return isIdCompressorTelemetryEnabled;
                    },
                };
            })();
            const compressorLogger = createSampledLogger(logger, idCompressorEventSampler);
            const pendingLocalState = context.pendingLocalState;
            if (pendingLocalState?.pendingIdCompressorState !== undefined) {
                return deserializeIdCompressor(pendingLocalState.pendingIdCompressorState, compressorLogger);
            }
            else if (serializedIdCompressor !== undefined) {
                return deserializeIdCompressor(serializedIdCompressor, createSessionId(), compressorLogger);
            }
            else {
                return createIdCompressor(compressorLogger);
            }
        };
        const compressionLz4 = compressionOptions.minimumBatchSizeInBytes !== Infinity &&
            compressionOptions.compressionAlgorithm === "lz4";
        const documentSchemaController = new DocumentsSchemaController(existing, protocolSequenceNumber, metadata?.documentSchema, {
            explicitSchemaControl,
            compressionLz4,
            idCompressorMode,
            opGroupingEnabled: enableGroupedBatching,
            disallowedVersions: [],
        }, (schema) => {
            runtime.onSchemaChange(schema);
        });
        const featureGatesForTelemetry = {};
        const runtime = new containerRuntimeCtor(context, registry, metadata, electedSummarizerData, chunks ?? [], aliases ?? [], {
            summaryOptions,
            gcOptions,
            loadSequenceNumberVerification,
            flushMode,
            compressionOptions,
            maxBatchSizeInBytes,
            chunkSizeInBytes,
            // Requires<> drops undefined from IdCompressorType
            enableRuntimeIdCompressor: enableRuntimeIdCompressor,
            enableGroupedBatching,
            explicitSchemaControl,
        }, containerScope, logger, existing, blobManagerSnapshot, context.storage, createIdCompressorFn, documentSchemaController, featureGatesForTelemetry, provideEntryPoint, requestHandler, undefined);
        runtime.blobManager.trackPendingStashedUploads().then(() => {
            // make sure we didn't reconnect before the promise resolved
            if (runtime.delayConnectClientId !== undefined && !runtime.disposed) {
                runtime.delayConnectClientId = undefined;
                runtime.setConnectionStateCore(true, runtime.delayConnectClientId);
            }
        }, (error) => runtime.closeFn(error));
        // Apply stashed ops with a reference sequence number equal to the sequence number of the snapshot,
        // or zero. This must be done before Container replays saved ops.
        await runtime.pendingStateManager.applyStashedOpsAt(runtimeSequenceNumber ?? 0);
        // Initialize the base state of the runtime before it's returned.
        await runtime.initializeBaseState();
        return runtime;
    }
    get clientId() {
        return this._getClientId();
    }
    get storage() {
        return this._storage;
    }
    get containerRuntime() {
        return this;
    }
    get flushMode() {
        return this._flushMode;
    }
    get scope() {
        return this.containerScope;
    }
    get IFluidDataStoreRegistry() {
        return this.registry;
    }
    get attachState() {
        return this._getAttachState();
    }
    /**
     * Current session schema - defines what options are on & off.
     * It's overlap of document schema (controlled by summary & ops) and options controlling this session.
     * For example, document schema might have compression ON, but feature gates / runtime options turn it Off.
     * In such case it will be off in session schema (i.e. this session should not use compression), but this client
     * has to deal with compressed ops as other clients might send them.
     * And in reverse, session schema can have compression Off, but feature gates / runtime options want it On.
     * In such case it will be off in session schema, however this client will propose change to schema, and once / if
     * this op rountrips, compression will be On. Client can't send compressed ops until it's change in schema.
     */
    get sessionSchema() {
        return this.documentsSchemaController.sessionSchema.runtime;
    }
    get idCompressorMode() {
        return this.sessionSchema.idCompressorMode;
    }
    /**
     * See IContainerRuntimeBase.idCompressor() for details.
     */
    get idCompressor() {
        // Expose ID Compressor only if it's On from the start.
        // If container uses delayed mode, then we can only expose generateDocumentUniqueId() and nothing else.
        // That's because any other usage will require immidiate loading of ID Compressor in next sessions in order
        // to reason over such things as session ID space.
        if (this.idCompressorMode === "on") {
            assert(this._idCompressor !== undefined, 0x8ea /* compressor should have been loaded */);
            return this._idCompressor;
        }
    }
    /**
     * See IContainerRuntimeBase.generateDocumentUniqueId() for details.
     */
    generateDocumentUniqueId() {
        return this._idCompressor?.generateDocumentUniqueId() ?? uuid();
    }
    get IFluidHandleContext() {
        return this.handleContext;
    }
    /**
     * Invokes the given callback and expects that no ops are submitted
     * until execution finishes. If an op is submitted, an error will be raised.
     *
     * Can be disabled by feature gate `Fluid.ContainerRuntime.DisableOpReentryCheck`
     *
     * @param callback - the callback to be invoked
     */
    ensureNoDataModelChanges(callback) {
        this.ensureNoDataModelChangesCalls++;
        try {
            return callback();
        }
        finally {
            this.ensureNoDataModelChangesCalls--;
        }
    }
    get connected() {
        return this._connected;
    }
    /** clientId of parent (non-summarizing) container that owns summarizer container */
    get summarizerClientId() {
        return this.summarizerClientElection?.electedClientId;
    }
    get disposed() {
        return this._disposed;
    }
    get summarizer() {
        assert(this._summarizer !== undefined, 0x257 /* "This is not summarizing container" */);
        return this._summarizer;
    }
    isSummariesDisabled() {
        return this.summaryConfiguration.state === "disabled";
    }
    getMaxOpsSinceLastSummary() {
        return this.summaryConfiguration.state !== "disabled"
            ? this.summaryConfiguration.maxOpsSinceLastSummary
            : 0;
    }
    getInitialSummarizerDelayMs() {
        // back-compat: initialSummarizerDelayMs was moved from ISummaryRuntimeOptions
        //   to ISummaryConfiguration in 0.60.
        if (this.runtimeOptions.summaryOptions.initialSummarizerDelayMs !== undefined) {
            return this.runtimeOptions.summaryOptions.initialSummarizerDelayMs;
        }
        return this.summaryConfiguration.state !== "disabled"
            ? this.summaryConfiguration.initialSummarizerDelayMs
            : 0;
    }
    /**
     * If false, loading or using a Tombstoned object should merely log, not fail.
     * @deprecated NOT SUPPORTED - hardcoded to return false since it's deprecated.
     */
    // eslint-disable-next-line @typescript-eslint/class-literal-property-style
    get gcTombstoneEnforcementAllowed() {
        return false;
    }
    /**
     * If true, throw an error when a tombstone data store is used.
     * @deprecated NOT SUPPORTED - hardcoded to return false since it's deprecated.
     */
    // eslint-disable-next-line @typescript-eslint/class-literal-property-style
    get gcThrowOnTombstoneUsage() {
        return false;
    }
    /***/
    constructor(context, registry, metadata, electedSummarizerData, chunks, dataStoreAliasMap, runtimeOptions, containerScope, 
    // Create a custom ITelemetryBaseLogger to output telemetry events.
    baseLogger, existing, blobManagerSnapshot, _storage, createIdCompressor, documentsSchemaController, featureGatesForTelemetry, provideEntryPoint, requestHandler, summaryConfiguration = {
        // the defaults
        ...DefaultSummaryConfiguration,
        // the runtime configuration overrides
        ...runtimeOptions.summaryOptions?.summaryConfigOverrides,
    }) {
        super();
        this.registry = registry;
        this.metadata = metadata;
        this.runtimeOptions = runtimeOptions;
        this.containerScope = containerScope;
        this.baseLogger = baseLogger;
        this._storage = _storage;
        this.createIdCompressor = createIdCompressor;
        this.documentsSchemaController = documentsSchemaController;
        this.requestHandler = requestHandler;
        this.summaryConfiguration = summaryConfiguration;
        this.imminentClosure = false;
        // We accumulate Id compressor Ops while Id compressor is not loaded yet (only for "delayed" mode)
        // Once it loads, it will process all such ops and we will stop accumulating further ops - ops will be processes as they come in.
        this.pendingIdCompressorOps = [];
        this.defaultMaxConsecutiveReconnects = 7;
        this._orderSequentiallyCalls = 0;
        this.flushTaskExists = false;
        this.consecutiveReconnects = 0;
        this.ensureNoDataModelChangesCalls = 0;
        this._disposed = false;
        this.emitDirtyDocumentEvent = true;
        this.defaultTelemetrySignalSampleCount = 100;
        this._perfSignalData = {
            signalsLost: 0,
            signalSequenceNumber: 0,
            signalTimestamp: 0,
            trackingSignalSequenceNumber: undefined,
        };
        /**
         * It a cache for holding mapping for loading groupIds with its snapshot from the service. Add expiry policy of 1 minute.
         * Starting with 1 min and based on recorded usage we can tweak it later on.
         */
        this.snapshotCacheForLoadingGroupIds = new PromiseCache({
            expiry: { policy: "absolute", durationMs: 60000 },
        });
        const { options, clientDetails, connected, baseSnapshot, submitFn, submitBatchFn, submitSummaryFn, submitSignalFn, disposeFn, closeFn, deltaManager, quorum, audience, loader, pendingLocalState, supportedFeatures, snapshotWithContents, } = context;
        this.logger = createChildLogger({ logger: this.baseLogger });
        this.mc = createChildMonitoringContext({
            logger: this.logger,
            namespace: "ContainerRuntime",
        });
        // If we support multiple algorithms in the future, then we would need to manage it here carefully.
        // We can use runtimeOptions.compressionOptions.compressionAlgorithm, but only if it's in the schema list!
        // If it's not in the list, then we will need to either use no compression, or fallback to some other (supported by format)
        // compression.
        const compressionOptions = {
            minimumBatchSizeInBytes: this.sessionSchema.compressionLz4
                ? runtimeOptions.compressionOptions.minimumBatchSizeInBytes
                : Number.POSITIVE_INFINITY,
            compressionAlgorithm: CompressionAlgorithms.lz4,
        };
        this.innerDeltaManager = deltaManager;
        // Here we could wrap/intercept on these functions to block/modify outgoing messages if needed.
        // This makes ContainerRuntime the final gatekeeper for outgoing messages.
        this.submitFn = submitFn;
        this.submitBatchFn = submitBatchFn;
        this.submitSummaryFn = submitSummaryFn;
        this.submitSignalFn = submitSignalFn;
        // TODO: After IContainerContext.options is removed, we'll just create a new blank object {} here.
        // Values are generally expected to be set from the runtime side.
        this.options = options ?? {};
        this.clientDetails = clientDetails;
        this.isSummarizerClient = this.clientDetails.type === summarizerClientType;
        this.loadedFromVersionId = context.getLoadedFromVersion()?.id;
        this._getClientId = () => context.clientId;
        this._getAttachState = () => context.attachState;
        this.getAbsoluteUrl = async (relativeUrl) => {
            if (context.getAbsoluteUrl === undefined) {
                throw new Error("Driver does not implement getAbsoluteUrl");
            }
            if (this.attachState !== AttachState.Attached) {
                return undefined;
            }
            return context.getAbsoluteUrl(relativeUrl);
        };
        // TODO: Consider that the Container could just listen to these events itself, or even more appropriately maybe the
        // customer should observe dirty state on the runtime (the owner of dirty state) directly, rather than on the IContainer.
        this.on("dirty", () => context.updateDirtyContainerState(true));
        this.on("saved", () => context.updateDirtyContainerState(false));
        // In old loaders without dispose functionality, closeFn is equivalent but will also switch container to readonly mode
        this.disposeFn = disposeFn ?? closeFn;
        // In cases of summarizer, we want to dispose instead since consumer doesn't interact with this container
        this.closeFn = this.isSummarizerClient ? this.disposeFn : closeFn;
        let loadSummaryNumber;
        // Get the container creation metadata. For new container, we initialize these. For existing containers,
        // get the values from the metadata blob.
        if (existing) {
            this.createContainerMetadata = {
                createContainerRuntimeVersion: metadata?.createContainerRuntimeVersion,
                createContainerTimestamp: metadata?.createContainerTimestamp,
            };
            // summaryNumber was renamed from summaryCount. For older docs that haven't been opened for a long time,
            // the count is reset to 0.
            loadSummaryNumber = metadata?.summaryNumber ?? 0;
        }
        else {
            this.createContainerMetadata = {
                createContainerRuntimeVersion: pkgVersion,
                createContainerTimestamp: Date.now(),
            };
            loadSummaryNumber = 0;
        }
        this.nextSummaryNumber = loadSummaryNumber + 1;
        this.messageAtLastSummary = lastMessageFromMetadata(metadata);
        // Note that we only need to pull the *initial* connected state from the context.
        // Later updates come through calls to setConnectionState.
        this._connected = connected;
        this.mc.logger.sendTelemetryEvent({
            eventName: "GCFeatureMatrix",
            metadataValue: JSON.stringify(metadata?.gcFeatureMatrix),
            inputs: JSON.stringify({
                gcOptions_gcGeneration: this.runtimeOptions.gcOptions[gcGenerationOptionName],
            }),
        });
        this.telemetryDocumentId = metadata?.telemetryDocumentId ?? uuid();
        this.disableAttachReorder = this.mc.config.getBoolean("Fluid.ContainerRuntime.disableAttachOpReorder");
        const opGroupingManager = new OpGroupingManager({
            groupedBatchingEnabled: this.groupedBatchingEnabled,
            opCountThreshold: this.mc.config.getNumber("Fluid.ContainerRuntime.GroupedBatchingOpCount") ?? 2,
            reentrantBatchGroupingEnabled: this.mc.config.getBoolean("Fluid.ContainerRuntime.GroupedBatchingReentrancy") ??
                true,
        }, this.mc.logger);
        const opSplitter = new OpSplitter(chunks, this.submitBatchFn, runtimeOptions.chunkSizeInBytes, runtimeOptions.maxBatchSizeInBytes, this.mc.logger);
        this.remoteMessageProcessor = new RemoteMessageProcessor(opSplitter, new OpDecompressor(this.mc.logger), opGroupingManager);
        const pendingRuntimeState = pendingLocalState;
        this.pendingStateManager = new PendingStateManager({
            applyStashedOp: this.applyStashedOp.bind(this),
            clientId: () => this.clientId,
            connected: () => this.connected,
            reSubmitBatch: this.reSubmitBatch.bind(this),
            isActiveConnection: () => this.innerDeltaManager.active,
            isAttached: () => this.attachState !== AttachState.Detached,
        }, pendingRuntimeState?.pending, this.logger);
        let outerDeltaManager;
        const useDeltaManagerOpsProxy = this.mc.config.getBoolean("Fluid.ContainerRuntime.DeltaManagerOpsProxy") !== false;
        // The summarizerDeltaManager Proxy is used to lie to the summarizer to convince it is in the right state as a summarizer client.
        const summarizerDeltaManagerProxy = new DeltaManagerSummarizerProxy(this.innerDeltaManager);
        outerDeltaManager = summarizerDeltaManagerProxy;
        // The DeltaManagerPendingOpsProxy is used to control the minimum sequence number
        // It allows us to lie to the layers below so that they can maintain enough local state for rebasing ops.
        if (useDeltaManagerOpsProxy) {
            const pendingOpsDeltaManagerProxy = new DeltaManagerPendingOpsProxy(summarizerDeltaManagerProxy, this.pendingStateManager);
            outerDeltaManager = pendingOpsDeltaManagerProxy;
        }
        this.deltaManager = outerDeltaManager;
        this.handleContext = new ContainerFluidHandleContext("", this);
        if (this.summaryConfiguration.state === "enabled") {
            this.validateSummaryHeuristicConfiguration(this.summaryConfiguration);
        }
        this.summariesDisabled = this.isSummariesDisabled();
        this.maxOpsSinceLastSummary = this.getMaxOpsSinceLastSummary();
        this.initialSummarizerDelayMs = this.getInitialSummarizerDelayMs();
        this.maxConsecutiveReconnects =
            this.mc.config.getNumber(maxConsecutiveReconnectsKey) ??
                this.defaultMaxConsecutiveReconnects;
        if (runtimeOptions.flushMode === FlushModeExperimental.Async &&
            supportedFeatures?.get("referenceSequenceNumbers") !== true) {
            // The loader does not support reference sequence numbers, falling back on FlushMode.TurnBased
            this.mc.logger.sendErrorEvent({ eventName: "FlushModeFallback" });
            this._flushMode = FlushMode.TurnBased;
        }
        else {
            this._flushMode = runtimeOptions.flushMode;
        }
        this.offlineEnabled =
            this.mc.config.getBoolean("Fluid.Container.enableOfflineLoad") ?? false;
        if (this.offlineEnabled && this._flushMode !== FlushMode.TurnBased) {
            const error = new UsageError("Offline mode is only supported in turn-based mode");
            this.closeFn(error);
            throw error;
        }
        if (context.attachState === AttachState.Attached) {
            const maxSnapshotCacheDurationMs = this._storage?.policies?.maximumCacheDurationMs;
            if (maxSnapshotCacheDurationMs !== undefined &&
                maxSnapshotCacheDurationMs > 5 * 24 * 60 * 60 * 1000) {
                // This is a runtime enforcement of what's already explicit in the policy's type itself,
                // which dictates the value is either undefined or exactly 5 days in ms.
                // As long as the actual value is less than 5 days, the assumptions GC makes here are valid.
                throw new UsageError("Driver's maximumCacheDurationMs policy cannot exceed 5 days");
            }
        }
        this.garbageCollector = GarbageCollector.create({
            runtime: this,
            gcOptions: this.runtimeOptions.gcOptions,
            baseSnapshot,
            baseLogger: this.mc.logger,
            existing,
            metadata,
            createContainerMetadata: this.createContainerMetadata,
            isSummarizerClient: this.isSummarizerClient,
            getNodePackagePath: async (nodePath) => this.getGCNodePackagePath(nodePath),
            getLastSummaryTimestampMs: () => this.messageAtLastSummary?.timestamp,
            readAndParseBlob: async (id) => readAndParse(this.storage, id),
            submitMessage: (message) => this.submit(message),
            sessionExpiryTimerStarted: pendingRuntimeState?.sessionExpiryTimerStarted,
        });
        const loadedFromSequenceNumber = this.deltaManager.initialSequenceNumber;
        this.summarizerNode = createRootSummarizerNodeWithGC(createChildLogger({ logger: this.logger, namespace: "SummarizerNode" }), 
        // Summarize function to call when summarize is called. Summarizer node always tracks summary state.
        async (fullTree, trackState, telemetryContext) => this.summarizeInternal(fullTree, trackState, telemetryContext), 
        // Latest change sequence number, no changes since summary applied yet
        loadedFromSequenceNumber, 
        // Summary reference sequence number, undefined if no summary yet
        baseSnapshot !== undefined ? loadedFromSequenceNumber : undefined, {
            // Must set to false to prevent sending summary handle which would be pointing to
            // a summary with an older protocol state.
            canReuseHandle: false,
            // If GC should not run, let the summarizer node know so that it does not track GC state.
            gcDisabled: !this.garbageCollector.shouldRunGC,
        }, 
        // Function to get GC data if needed. This will always be called by the root summarizer node to get GC data.
        async (fullGC) => this.getGCDataInternal(fullGC), 
        // Function to get the GC details from the base snapshot we loaded from.
        async () => this.garbageCollector.getBaseGCDetails());
        if (baseSnapshot) {
            this.summarizerNode.updateBaseSummaryState(baseSnapshot);
        }
        const parentContext = wrapContext(this);
        if (snapshotWithContents !== undefined) {
            this.isSnapshotInstanceOfISnapshot = true;
        }
        // Due to a mismatch between different layers in terms of
        // what is the interface of passing signals, we need the
        // downstream stores to wrap the signal.
        parentContext.submitSignal = (type, content, targetClientId) => {
            const envelope1 = content;
            const envelope2 = this.createNewSignalEnvelope(envelope1.address, type, envelope1.contents);
            return this.submitSignalFn(envelope2, targetClientId);
        };
        let snapshot = getSummaryForDatastores(baseSnapshot, metadata);
        if (snapshot !== undefined && snapshotWithContents !== undefined) {
            snapshot = {
                ...snapshotWithContents,
                snapshotTree: snapshot,
            };
        }
        this.channelCollection = new ChannelCollection(snapshot, parentContext, this.mc.logger, (props) => this.garbageCollector.nodeUpdated({
            ...props,
            timestampMs: props.timestampMs ?? this.getCurrentReferenceTimestampMs(),
        }), (path) => this.garbageCollector.isNodeDeleted(path), new Map(dataStoreAliasMap), async (runtime) => provideEntryPoint);
        this.blobManager = new BlobManager({
            routeContext: this.handleContext,
            snapshot: blobManagerSnapshot,
            getStorage: () => this.storage,
            sendBlobAttachOp: (localId, blobId) => {
                if (!this.disposed) {
                    this.submit({ type: ContainerMessageType.BlobAttach, contents: undefined }, undefined, {
                        localId,
                        blobId,
                    });
                }
            },
            blobRequested: (blobPath) => this.garbageCollector.nodeUpdated({
                node: { type: "Blob", path: blobPath },
                reason: "Loaded",
                timestampMs: this.getCurrentReferenceTimestampMs(),
            }),
            isBlobDeleted: (blobPath) => this.garbageCollector.isNodeDeleted(blobPath),
            runtime: this,
            stashedBlobs: pendingRuntimeState?.pendingAttachmentBlobs,
            closeContainer: (error) => this.closeFn(error),
        });
        this.scheduleManager = new ScheduleManager(this.innerDeltaManager, this, () => this.clientId, createChildLogger({ logger: this.logger, namespace: "ScheduleManager" }));
        const disablePartialFlush = this.mc.config.getBoolean("Fluid.ContainerRuntime.DisablePartialFlush");
        const legacySendBatchFn = makeLegacySendBatchFn(this.submitFn, this.innerDeltaManager);
        this.outbox = new Outbox({
            shouldSend: () => this.canSendOps(),
            pendingStateManager: this.pendingStateManager,
            submitBatchFn: this.submitBatchFn,
            legacySendBatchFn,
            compressor: new OpCompressor(this.mc.logger),
            splitter: opSplitter,
            config: {
                compressionOptions,
                maxBatchSizeInBytes: runtimeOptions.maxBatchSizeInBytes,
                disablePartialFlush: disablePartialFlush === true,
            },
            logger: this.mc.logger,
            groupingManager: opGroupingManager,
            getCurrentSequenceNumbers: () => ({
                referenceSequenceNumber: this.deltaManager.lastSequenceNumber,
                clientSequenceNumber: this._processedClientSequenceNumber,
            }),
            reSubmit: this.reSubmit.bind(this),
            opReentrancy: () => this.ensureNoDataModelChangesCalls > 0,
            closeContainer: this.closeFn,
        });
        this._quorum = quorum;
        this._quorum.on("removeMember", (clientId) => {
            this.remoteMessageProcessor.clearPartialMessagesFor(clientId);
        });
        this._audience = audience;
        if (audience.getSelf === undefined) {
            // back-compat, added in 2.0 RC3.
            // Purpose: deal with cases when we run against old loader that does not have newly added capabilities
            audience.getSelf = () => {
                const clientId = this._getClientId();
                return clientId === undefined
                    ? undefined
                    : ({
                        clientId,
                        client: audience.getMember(clientId),
                    });
            };
            let oldClientId = this.clientId;
            this.on("connected", () => {
                const clientId = this.clientId;
                assert(clientId !== undefined, 0x975 /* can't be undefined */);
                audience.emit("selfChanged", { clientId: oldClientId }, { clientId, client: audience.getMember(clientId) });
                oldClientId = clientId;
            });
        }
        const closeSummarizerDelayOverride = this.mc.config.getNumber("Fluid.ContainerRuntime.Test.CloseSummarizerDelayOverrideMs");
        this.closeSummarizerDelayMs =
            closeSummarizerDelayOverride ?? defaultCloseSummarizerDelayMs;
        this.summaryCollection = new SummaryCollection(this.deltaManager, this.logger);
        this.dirtyContainer =
            this.attachState !== AttachState.Attached || this.hasPendingMessages();
        context.updateDirtyContainerState(this.dirtyContainer);
        if (this.summariesDisabled) {
            this.mc.logger.sendTelemetryEvent({ eventName: "SummariesDisabled" });
        }
        else {
            const orderedClientLogger = createChildLogger({
                logger: this.logger,
                namespace: "OrderedClientElection",
            });
            const orderedClientCollection = new OrderedClientCollection(orderedClientLogger, this.innerDeltaManager, this._quorum);
            const orderedClientElectionForSummarizer = new OrderedClientElection(orderedClientLogger, orderedClientCollection, electedSummarizerData ?? this.innerDeltaManager.lastSequenceNumber, SummarizerClientElection.isClientEligible, this.mc.config.getBoolean("Fluid.ContainerRuntime.OrderedClientElection.EnablePerformanceEvents"));
            this.summarizerClientElection = new SummarizerClientElection(orderedClientLogger, this.summaryCollection, orderedClientElectionForSummarizer, this.maxOpsSinceLastSummary);
            if (this.isSummarizerClient) {
                this._summarizer = new Summarizer(this /* ISummarizerRuntime */, () => this.summaryConfiguration, this /* ISummarizerInternalsProvider */, this.handleContext, this.summaryCollection, async (runtime) => RunWhileConnectedCoordinator.create(runtime, 
                // Summarization runs in summarizer client and needs access to the real (non-proxy) active
                // information. The proxy delta manager would always return false for summarizer client.
                () => this.innerDeltaManager.active));
            }
            else if (SummarizerClientElection.clientDetailsPermitElection(this.clientDetails)) {
                // Only create a SummaryManager and SummarizerClientElection
                // if summaries are enabled and we are not the summarizer client.
                const defaultAction = () => {
                    if (this.summaryCollection.opsSinceLastAck > this.maxOpsSinceLastSummary) {
                        this.mc.logger.sendTelemetryEvent({ eventName: "SummaryStatus:Behind" });
                        // unregister default to no log on every op after falling behind
                        // and register summary ack handler to re-register this handler
                        // after successful summary
                        this.summaryCollection.once(MessageType.SummaryAck, () => {
                            this.mc.logger.sendTelemetryEvent({
                                eventName: "SummaryStatus:CaughtUp",
                            });
                            // we've caught up, so re-register the default action to monitor for
                            // falling behind, and unregister ourself
                            this.summaryCollection.on("default", defaultAction);
                        });
                        this.summaryCollection.off("default", defaultAction);
                    }
                };
                this.summaryCollection.on("default", defaultAction);
                // Create the SummaryManager and mark the initial state
                this.summaryManager = new SummaryManager(this.summarizerClientElection, this, // IConnectedState
                this.summaryCollection, this.logger, this.formCreateSummarizerFn(loader), new Throttler(60 * 1000, // 60 sec delay window
                30 * 1000, // 30 sec max delay
                // throttling function increases exponentially (0ms, 40ms, 80ms, 160ms, etc)
                formExponentialFn({ coefficient: 20, initialDelay: 0 })), {
                    initialDelayMs: this.initialSummarizerDelayMs,
                });
                this.summaryManager.on("summarize", (eventProps) => {
                    this.emit("summarize", eventProps);
                });
                this.summaryManager.start();
            }
        }
        // logging hardware telemetry
        this.logger.sendTelemetryEvent({
            eventName: "DeviceSpec",
            ...getDeviceSpec(),
        });
        this.mc.logger.sendTelemetryEvent({
            eventName: "ContainerLoadStats",
            ...this.createContainerMetadata,
            ...this.channelCollection.containerLoadStats,
            summaryNumber: loadSummaryNumber,
            summaryFormatVersion: metadata?.summaryFormatVersion,
            disableIsolatedChannels: metadata?.disableIsolatedChannels,
            gcVersion: metadata?.gcFeature,
            options: JSON.stringify(runtimeOptions),
            idCompressorModeMetadata: metadata?.documentSchema?.runtime?.idCompressorMode,
            idCompressorMode: this.idCompressorMode,
            sessionRuntimeSchema: JSON.stringify(this.sessionSchema),
            featureGates: JSON.stringify({
                ...featureGatesForTelemetry,
                disableAttachReorder: this.disableAttachReorder,
                disablePartialFlush,
                closeSummarizerDelayOverride,
            }),
            telemetryDocumentId: this.telemetryDocumentId,
            groupedBatchingEnabled: this.groupedBatchingEnabled,
            initialSequenceNumber: this.deltaManager.initialSequenceNumber,
        });
        ReportOpPerfTelemetry(this.clientId, this.deltaManager, this, this.logger);
        BindBatchTracker(this, this.logger);
        this.entryPoint = new LazyPromise(async () => {
            if (this.isSummarizerClient) {
                assert(this._summarizer !== undefined, 0x5bf /* Summarizer object is undefined in a summarizer client */);
                return this._summarizer;
            }
            return provideEntryPoint(this);
        });
        // If we loaded from pending state, then we need to skip any ops that are already accounted in such
        // saved state, i.e. all the ops marked by Loader layer sa savedOp === true.
        this.skipSavedCompressorOps = pendingRuntimeState?.pendingIdCompressorState !== undefined;
    }
    onSchemaChange(schema) {
        this.logger.sendTelemetryEvent({
            eventName: "SchemaChangeAccept",
            sessionRuntimeSchema: JSON.stringify(schema),
        });
        // Most of the settings will be picked up only by new sessions (i.e. after reload).
        // We can make it better in the future (i.e. start to use op compression right away), but for simplicity
        // this is not done.
        // But ID compressor is special. It's possible, that in future, we will remove "stickiness" of ID compressor setting
        // and will allow to start using it. If that were to happen, we want to ensure that we do not break eventual consistency
        // promises. To do so, we need to initialize id compressor right away.
        // As it's implemented right now (with async initialization), this will only work for "off" -> "delayed" transitions.
        // Anything else is too risky, and requires ability to initialize ID compressor synchronously!
        if (schema.runtime.idCompressorMode !== undefined) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.loadIdCompressor();
        }
    }
    getCreateChildSummarizerNodeFn(id, createParam) {
        return (summarizeInternal, getGCDataFn) => this.summarizerNode.createChild(summarizeInternal, id, createParam, undefined, getGCDataFn);
    }
    deleteChildSummarizerNode(id) {
        return this.summarizerNode.deleteChild(id);
    }
    /* IFluidParentContext APIs that should not be called on Root */
    makeLocallyVisible() {
        assert(false, 0x8eb /* should not be called */);
    }
    setChannelDirty(address) {
        assert(false, 0x909 /* should not be called */);
    }
    /**
     * Initializes the state from the base snapshot this container runtime loaded from.
     */
    async initializeBaseState() {
        if (this.idCompressorMode === "on" ||
            (this.idCompressorMode === "delayed" && this.connected)) {
            this._idCompressor = await this.createIdCompressor();
            // This is called from loadRuntime(), long before we process any ops, so there should be no ops accumulated yet.
            assert(this.pendingIdCompressorOps.length === 0, 0x8ec /* no pending ops */);
        }
        await this.garbageCollector.initializeBaseState();
    }
    dispose(error) {
        if (this._disposed) {
            return;
        }
        this._disposed = true;
        this.mc.logger.sendTelemetryEvent({
            eventName: "ContainerRuntimeDisposed",
            isDirty: this.isDirty,
            lastSequenceNumber: this.deltaManager.lastSequenceNumber,
            attachState: this.attachState,
        }, error);
        if (this.summaryManager !== undefined) {
            this.summaryManager.dispose();
        }
        this.garbageCollector.dispose();
        this._summarizer?.dispose();
        this.channelCollection.dispose();
        this.pendingStateManager.dispose();
        this.emit("dispose");
        this.removeAllListeners();
    }
    /**
     * Api to fetch the snapshot from the service for a loadingGroupIds.
     * @param loadingGroupIds - LoadingGroupId for which the snapshot is asked for.
     * @param pathParts - Parts of the path, which we want to extract from the snapshot tree.
     * @returns - snapshotTree and the sequence number of the snapshot.
     */
    async getSnapshotForLoadingGroupId(loadingGroupIds, pathParts) {
        const sortedLoadingGroupIds = loadingGroupIds.sort();
        assert(this.storage.getSnapshot !== undefined, 0x8ed /* getSnapshot api should be defined if used */);
        let loadedFromCache = true;
        // Lookup up in the cache, if not present then make the network call as multiple datastores could
        // be in same loading group. So, once we have fetched the snapshot for that loading group on
        // any request, then cache that as same group could be requested in future too.
        const snapshot = await this.snapshotCacheForLoadingGroupIds.addOrGet(sortedLoadingGroupIds.join(), async () => {
            assert(this.storage.getSnapshot !== undefined, 0x8ee /* getSnapshot api should be defined if used */);
            loadedFromCache = false;
            return this.storage.getSnapshot({
                cacheSnapshot: false,
                scenarioName: "snapshotForLoadingGroupId",
                loadingGroupIds: sortedLoadingGroupIds,
            });
        });
        this.logger.sendTelemetryEvent({
            eventName: "GroupIdSnapshotFetched",
            details: JSON.stringify({
                fromCache: loadedFromCache,
                loadingGroupIds: loadingGroupIds.join(","),
            }),
        });
        // Find the snapshotTree inside the returned snapshot based on the path as given in the request.
        const hasIsolatedChannels = rootHasIsolatedChannels(this.metadata);
        const snapshotTreeForPath = this.getSnapshotTreeForPath(snapshot.snapshotTree, pathParts, hasIsolatedChannels);
        assert(snapshotTreeForPath !== undefined, 0x8ef /* no snapshotTree for the path */);
        const snapshotSeqNumber = snapshot.sequenceNumber;
        assert(snapshotSeqNumber !== undefined, 0x8f0 /* snapshotSeqNumber should be present */);
        // This assert fires if we get a snapshot older than the snapshot we loaded from. This is a service issue.
        // Snapshots should only move forward. If we observe an older snapshot than the one we loaded from, then likely
        // the file has been overwritten or service lost data.
        if (snapshotSeqNumber < this.deltaManager.initialSequenceNumber) {
            throw DataProcessingError.create("Downloaded snapshot older than snapshot we loaded from", "getSnapshotForLoadingGroupId", undefined, {
                loadingGroupIds: sortedLoadingGroupIds.join(","),
                snapshotSeqNumber,
                initialSequenceNumber: this.deltaManager.initialSequenceNumber,
            });
        }
        // If the snapshot is ahead of the last seq number of the delta manager, then catch up before
        // returning the snapshot.
        if (snapshotSeqNumber > this.deltaManager.lastSequenceNumber) {
            // If this is a summarizer client, which is trying to load a group and it finds that there is
            // another snapshot from which the summarizer loaded and it is behind, then just give up as
            // the summarizer state is not up to date.
            // This should be a recoverable scenario and shouldn't happen as we should process the ack first.
            if (this.isSummarizerClient) {
                throw new Error("Summarizer client behind, loaded newer snapshot with loadingGroupId");
            }
            // We want to catchup from sequenceNumber to targetSequenceNumber
            const props = {
                eventName: "GroupIdSnapshotCatchup",
                loadingGroupIds: sortedLoadingGroupIds.join(","),
                targetSequenceNumber: snapshotSeqNumber, // This is so we reuse some columns in telemetry
                sequenceNumber: this.deltaManager.lastSequenceNumber, // This is so we reuse some columns in telemetry
            };
            const event = PerformanceEvent.start(this.mc.logger, {
                ...props,
            });
            // If the inbound deltas queue is paused or disconnected, we expect a reconnect and unpause
            // as long as it's not a summarizer client.
            if (this.deltaManager.inbound.paused) {
                props.inboundPaused = this.deltaManager.inbound.paused; // reusing telemetry
            }
            const defP = new Deferred();
            this.deltaManager.on("op", (message) => {
                if (message.sequenceNumber >= snapshotSeqNumber) {
                    defP.resolve(true);
                }
            });
            await defP.promise;
            event.end(props);
        }
        return { snapshotTree: snapshotTreeForPath, sequenceNumber: snapshotSeqNumber };
    }
    /**
     * Api to find a snapshot tree inside a bigger snapshot tree based on the path in the pathParts array.
     * @param snapshotTree - snapshot tree to look into.
     * @param pathParts - Part of the path, which we want to extract from the snapshot tree.
     * @param hasIsolatedChannels - whether the channels are present inside ".channels" subtree. Older
     * snapshots will not have trees inside ".channels", so check that.
     * @returns - requested snapshot tree based on the path parts.
     */
    getSnapshotTreeForPath(snapshotTree, pathParts, hasIsolatedChannels) {
        let childTree = snapshotTree;
        for (const part of pathParts) {
            if (hasIsolatedChannels) {
                // TODO Why are we non null asserting here
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                childTree = childTree.trees[channelsTreeName];
            }
            // TODO Why are we non null asserting here
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            childTree = childTree.trees[part];
        }
        return childTree;
    }
    /**
     * Notifies this object about the request made to the container.
     * @param request - Request made to the handler.
     * @deprecated Will be removed in future major release. This method needs to stay private until LTS version of Loader moves to "2.0.0-internal.7.0.0".
     */
    // @ts-expect-error expected to be used by LTS Loaders and Containers
    async request(request) {
        try {
            const parser = RequestParser.create(request);
            const id = parser.pathParts[0];
            if (id === summarizerRequestUrl && parser.pathParts.length === 1) {
                if (this._summarizer !== undefined) {
                    return {
                        status: 200,
                        mimeType: "fluid/object",
                        value: this.summarizer,
                    };
                }
                return create404Response(request);
            }
            if (this.requestHandler !== undefined) {
                // eslint-disable-next-line @typescript-eslint/return-await -- Adding an await here causes test failures
                return this.requestHandler(parser, this);
            }
            return create404Response(request);
        }
        catch (error) {
            return exceptionToResponse(error);
        }
    }
    /**
     * Resolves URI representing handle
     * @param request - Request made to the handler.
     */
    async resolveHandle(request) {
        try {
            const requestParser = RequestParser.create(request);
            const id = requestParser.pathParts[0];
            if (id === "_channels") {
                // eslint-disable-next-line @typescript-eslint/return-await -- Adding an await here causes test failures
                return this.resolveHandle(requestParser.createSubRequest(1));
            }
            if (id === blobManagerBasePath && requestParser.isLeaf(2)) {
                // TODO why are we non null asserting here?
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const blob = await this.blobManager.getBlob(requestParser.pathParts[1]);
                return blob
                    ? {
                        status: 200,
                        mimeType: "fluid/object",
                        value: blob,
                    }
                    : create404Response(request);
            }
            else if (requestParser.pathParts.length > 0) {
                return await this.channelCollection.request(request);
            }
            return create404Response(request);
        }
        catch (error) {
            return exceptionToResponse(error);
        }
    }
    /**
     * {@inheritDoc @fluidframework/container-definitions#IRuntime.getEntryPoint}
     */
    async getEntryPoint() {
        return this.entryPoint;
    }
    internalId(maybeAlias) {
        return this.channelCollection.internalId(maybeAlias);
    }
    /** Adds the container's metadata to the given summary tree. */
    addMetadataToSummary(summaryTree) {
        // The last message processed at the time of summary. If there are no new messages, use the message from the
        // last summary.
        const message = extractSummaryMetadataMessage(this.deltaManager.lastMessage) ??
            this.messageAtLastSummary;
        const documentSchema = this.documentsSchemaController.summarizeDocumentSchema(this.deltaManager.lastSequenceNumber);
        // Is document schema explicit control on?
        const explicitSchemaControl = documentSchema?.runtime.explicitSchemaControl;
        const metadata = {
            ...this.createContainerMetadata,
            // Increment the summary number for the next summary that will be generated.
            summaryNumber: this.nextSummaryNumber++,
            summaryFormatVersion: 1,
            ...this.garbageCollector.getMetadata(),
            telemetryDocumentId: this.telemetryDocumentId,
            // If explicit document schema control is not on, use legacy way to supply last message (using 'message' property).
            // Otherwise use new 'lastMessage' property, but also put content into the 'message' property that cases old
            // runtimes (that preceed document schema control capabilities) to close container on load due to mismatch in
            // last message's sequence number.
            // See also lastMessageFromMetadata()
            message: explicitSchemaControl
                ? { sequenceNumber: -1 }
                : message,
            lastMessage: explicitSchemaControl ? message : undefined,
            documentSchema,
        };
        addBlobToSummary(summaryTree, metadataBlobName, JSON.stringify(metadata));
    }
    addContainerStateToSummary(summaryTree, fullTree, trackState, telemetryContext) {
        this.addMetadataToSummary(summaryTree);
        if (this._idCompressor) {
            const idCompressorState = JSON.stringify(this._idCompressor.serialize(false));
            addBlobToSummary(summaryTree, idCompressorBlobName, idCompressorState);
        }
        if (this.remoteMessageProcessor.partialMessages.size > 0) {
            const content = JSON.stringify([...this.remoteMessageProcessor.partialMessages]);
            addBlobToSummary(summaryTree, chunksBlobName, content);
        }
        const dataStoreAliases = this.channelCollection.aliases;
        if (dataStoreAliases.size > 0) {
            addBlobToSummary(summaryTree, aliasBlobName, JSON.stringify([...dataStoreAliases]));
        }
        if (this.summarizerClientElection) {
            const electedSummarizerContent = JSON.stringify(this.summarizerClientElection?.serialize());
            addBlobToSummary(summaryTree, electedSummarizerBlobName, electedSummarizerContent);
        }
        const blobManagerSummary = this.blobManager.summarize();
        // Some storage (like git) doesn't allow empty tree, so we can omit it.
        // and the blob manager can handle the tree not existing when loading
        if (Object.keys(blobManagerSummary.summary.tree).length > 0) {
            addSummarizeResultToSummary(summaryTree, blobsTreeName, blobManagerSummary);
        }
        const gcSummary = this.garbageCollector.summarize(fullTree, trackState, telemetryContext);
        if (gcSummary !== undefined) {
            addSummarizeResultToSummary(summaryTree, gcTreeKey, gcSummary);
        }
    }
    // Track how many times the container tries to reconnect with pending messages.
    // This happens when the connection state is changed and we reset the counter
    // when we are able to process a local op or when there are no pending messages.
    // If this counter reaches a max, it's a good indicator that the container
    // is not making progress and it is stuck in a retry loop.
    shouldContinueReconnecting() {
        if (this.maxConsecutiveReconnects <= 0) {
            // Feature disabled, we never stop reconnecting
            return true;
        }
        if (!this.hasPendingMessages()) {
            // If there are no pending messages, we can always reconnect
            this.resetReconnectCount();
            return true;
        }
        if (this.consecutiveReconnects === Math.floor(this.maxConsecutiveReconnects / 2)) {
            // If we're halfway through the max reconnects, send an event in order
            // to better identify false positives, if any. If the rate of this event
            // matches Container Close count below, we can safely cut down
            // maxConsecutiveReconnects to half.
            this.mc.logger.sendTelemetryEvent({
                eventName: "ReconnectsWithNoProgress",
                attempts: this.consecutiveReconnects,
                pendingMessages: this.pendingMessagesCount,
            });
        }
        return this.consecutiveReconnects < this.maxConsecutiveReconnects;
    }
    resetReconnectCount() {
        this.consecutiveReconnects = 0;
    }
    replayPendingStates() {
        // We need to be able to send ops to replay states
        if (!this.canSendOps()) {
            return;
        }
        // We need to temporary clear the dirty flags and disable
        // dirty state change events to detect whether replaying ops
        // has any effect.
        // Save the old state, reset to false, disable event emit
        const oldState = this.dirtyContainer;
        this.dirtyContainer = false;
        assert(this.emitDirtyDocumentEvent, 0x127 /* "dirty document event not set on replay" */);
        this.emitDirtyDocumentEvent = false;
        let newState;
        try {
            this.submitIdAllocationOpIfNeeded(true);
            // replay the ops
            this.pendingStateManager.replayPendingStates();
        }
        finally {
            // Save the new start and restore the old state, re-enable event emit
            newState = this.dirtyContainer;
            this.dirtyContainer = oldState;
            this.emitDirtyDocumentEvent = true;
        }
        // Officially transition from the old state to the new state.
        this.updateDocumentDirtyState(newState);
    }
    /**
     * Parse an op's type and actual content from given serialized content
     * ! Note: this format needs to be in-line with what is set in the "ContainerRuntime.submit(...)" method
     */
    // TODO: markfields: confirm Local- versus Outbound- ContainerRuntimeMessage typing
    parseLocalOpContent(serializedContents) {
        assert(serializedContents !== undefined, 0x6d5 /* content must be defined */);
        const message = JSON.parse(serializedContents);
        assert(message.type !== undefined, 0x6d6 /* incorrect op content format */);
        return message;
    }
    async applyStashedOp(serializedOpContent) {
        // Need to parse from string for back-compat
        const opContents = this.parseLocalOpContent(serializedOpContent);
        switch (opContents.type) {
            case ContainerMessageType.FluidDataStoreOp:
            case ContainerMessageType.Attach:
            case ContainerMessageType.Alias:
                return this.channelCollection.applyStashedOp(opContents);
            case ContainerMessageType.IdAllocation:
                // IDs allocation ops in stashed state are ignored because the tip state of the compressor
                // is serialized into the pending state. This is done because generation of new IDs during
                // stashed op application (or, later, resubmit) must generate new IDs and if the compressor
                // was loaded from a state serialized at the same time as the summary tree in the stashed state
                // then it would generate IDs that collide with any in later stashed ops.
                // In the future, IdCompressor could be extended to have an "applyStashedOp" or similar method
                // and the runtime could filter out all ID allocation ops from the stashed state and apply them
                // before applying the rest of the stashed ops. This would accomplish the same thing but with
                // better performance in future incremental stashed state creation.
                assert(this.idCompressorMode !== undefined, 0x8f1 /* ID compressor should be in use */);
                return;
            case ContainerMessageType.DocumentSchemaChange:
                return;
            case ContainerMessageType.BlobAttach:
                return;
            case ContainerMessageType.Rejoin:
                throw new Error("rejoin not expected here");
            case ContainerMessageType.GC:
                // GC op is only sent in summarizer which should never have stashed ops.
                throw new LoggingError("GC op not expected to be stashed in summarizer");
            default: {
                // This should be extremely rare for stashed ops.
                // It would require a newer runtime stashing ops and then an older one applying them,
                // e.g. if an app rolled back its container version
                const compatBehavior = opContents.compatDetails?.behavior;
                if (!compatBehaviorAllowsMessageType(opContents.type, compatBehavior)) {
                    const error = DataProcessingError.create("Stashed runtime message of unexpected type", "applyStashedOp", undefined /* sequencedMessage */, {
                        messageDetails: JSON.stringify({
                            type: opContents.type,
                            compatBehavior,
                        }),
                    });
                    this.closeFn(error);
                    throw error;
                }
                // Note: Even if its compat behavior allows it, we don't know how to apply this stashed op.
                // All we can do is ignore it (similar to on process).
            }
        }
    }
    async loadIdCompressor() {
        if (this._idCompressor === undefined &&
            this.idCompressorMode !== undefined &&
            this._loadIdCompressor === undefined) {
            this._loadIdCompressor = this.createIdCompressor()
                .then((compressor) => {
                // Finalize any ranges we received while the compressor was turned off.
                const ops = this.pendingIdCompressorOps;
                this.pendingIdCompressorOps = [];
                for (const range of ops) {
                    compressor.finalizeCreationRange(range);
                }
                assert(this.pendingIdCompressorOps.length === 0, 0x976 /* No new ops added */);
                this._idCompressor = compressor;
            })
                .catch((error) => {
                this.logger.sendErrorEvent({ eventName: "IdCompressorDelayedLoad" }, error);
                throw error;
            });
        }
        return this._loadIdCompressor;
    }
    setConnectionState(connected, clientId) {
        // Validate we have consistent state
        const currentClientId = this._audience.getSelf()?.clientId;
        assert(clientId === currentClientId, 0x977 /* input clientId does not match Audience */);
        assert(this.clientId === currentClientId, 0x978 /* this.clientId does not match Audience */);
        if (connected && this.idCompressorMode === "delayed") {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.loadIdCompressor();
        }
        if (connected === false && this.delayConnectClientId !== undefined) {
            this.delayConnectClientId = undefined;
            this.mc.logger.sendTelemetryEvent({
                eventName: "UnsuccessfulConnectedTransition",
            });
            // Don't propagate "disconnected" event because we didn't propagate the previous "connected" event
            return;
        }
        if (!connected) {
            this.documentsSchemaController.onDisconnect();
        }
        // If there are stashed blobs in the pending state, we need to delay
        // propagation of the "connected" event until we have uploaded them to
        // ensure we don't submit ops referencing a blob that has not been uploaded
        const connecting = connected && !this._connected;
        if (connecting && this.blobManager.hasPendingStashedUploads()) {
            assert(!this.delayConnectClientId, 0x791 /* Connect event delay must be canceled before subsequent connect event */);
            assert(!!clientId, 0x792 /* Must have clientId when connecting */);
            this.delayConnectClientId = clientId;
            return;
        }
        this.setConnectionStateCore(connected, clientId);
    }
    setConnectionStateCore(connected, clientId) {
        assert(!this.delayConnectClientId, 0x394 /* connect event delay must be cleared before propagating connect event */);
        this.verifyNotClosed();
        // There might be no change of state due to Container calling this API after loading runtime.
        const changeOfState = this._connected !== connected;
        const reconnection = changeOfState && !connected;
        // We need to flush the ops currently collected by Outbox to preserve original order.
        // This flush NEEDS to happen before we set the ContainerRuntime to "connected".
        // We want these ops to get to the PendingStateManager without sending to service and have them return to the Outbox upon calling "replayPendingStates".
        if (changeOfState && connected) {
            this.flush();
        }
        this._connected = connected;
        if (!connected) {
            this._perfSignalData.signalsLost = 0;
            this._perfSignalData.signalTimestamp = 0;
            this._perfSignalData.trackingSignalSequenceNumber = undefined;
        }
        else {
            assert(this.attachState === AttachState.Attached, 0x3cd /* Connection is possible only if container exists in storage */);
        }
        // Fail while disconnected
        if (reconnection) {
            this.consecutiveReconnects++;
            if (!this.shouldContinueReconnecting()) {
                this.closeFn(DataProcessingError.create("Runtime detected too many reconnects with no progress syncing local ops.", "setConnectionState", undefined, {
                    dataLoss: 1,
                    attempts: this.consecutiveReconnects,
                    pendingMessages: this.pendingMessagesCount,
                }));
                return;
            }
        }
        if (changeOfState) {
            this.replayPendingStates();
        }
        this.channelCollection.setConnectionState(connected, clientId);
        this.garbageCollector.setConnectionState(connected, clientId);
        raiseConnectedEvent(this.mc.logger, this, connected, clientId);
    }
    async notifyOpReplay(message) {
        await this.pendingStateManager.applyStashedOpsAt(message.sequenceNumber);
    }
    /**
     * Processes the op.
     * @param messageCopy - Sequenced message for a distributed document.
     * @param local - true if the message was originally generated by the client receiving it.
     */
    process({ ...messageCopy }, local) {
        // spread operator above ensure we make a shallow copy of message, as the processing flow will modify it.
        // There might be multiple container instances receiving the same message.
        this.verifyNotClosed();
        // Whether or not the message appears to be a runtime message from an up-to-date client.
        // It may be a legacy runtime message (ie already unpacked and ContainerMessageType)
        // or something different, like a system message.
        const hasModernRuntimeMessageEnvelope = messageCopy.type === MessageType.Operation;
        const savedOp = messageCopy.metadata?.savedOp;
        const logLegacyCase = getSingleUseLegacyLogCallback(this.logger, messageCopy.type);
        // We expect runtime messages to have JSON contents - deserialize it in place.
        ensureContentsDeserialized(messageCopy, hasModernRuntimeMessageEnvelope, logLegacyCase);
        if (hasModernRuntimeMessageEnvelope) {
            // If the message has the modern message envelope, then process it here.
            // Here we unpack the message (decompress, unchunk, and/or ungroup) into a batch of messages with ContainerMessageType
            const inboundBatch = this.remoteMessageProcessor.process(messageCopy, logLegacyCase);
            if (inboundBatch === undefined) {
                // This means the incoming message is an incomplete part of a message or batch
                // and we need to process more messages before the rest of the system can understand it.
                return;
            }
            // Reach out to PendingStateManager to zip localOpMetadata into the message list if it's a local batch
            const messagesWithPendingState = this.pendingStateManager.processInboundBatch(inboundBatch, local);
            if (messagesWithPendingState.length > 0) {
                messagesWithPendingState.forEach(({ message, localOpMetadata }) => {
                    const msg = {
                        message,
                        local,
                        isRuntimeMessage: true,
                        savedOp,
                        localOpMetadata,
                    };
                    this.ensureNoDataModelChanges(() => this.processRuntimeMessage(msg));
                });
            }
            else {
                this.ensureNoDataModelChanges(() => this.processEmptyBatch(inboundBatch, local));
            }
        }
        else {
            // Check if message.type is one of values in ContainerMessageType
            // eslint-disable-next-line import/no-deprecated
            if (isRuntimeMessage(messageCopy)) {
                // Legacy op received
                this.ensureNoDataModelChanges(() => this.processRuntimeMessage({
                    message: messageCopy,
                    local,
                    isRuntimeMessage: true,
                    savedOp,
                }));
            }
            else {
                // A non container runtime message (like other system ops - join, ack, leave, nack etc.)
                this.ensureNoDataModelChanges(() => this.observeNonRuntimeMessage({
                    message: messageCopy,
                    local,
                    isRuntimeMessage: false,
                    savedOp,
                }));
            }
        }
    }
    /**
     * Processes messages that are intended for the runtime layer to process.
     * It redirects the message to the correct subsystem for processing, and implement other side effects
     * @param messageWithContext - message to process with additional context and isRuntimeMessage prop as true
     */
    processRuntimeMessage(messageWithContext) {
        const { message, local, localOpMetadata } = messageWithContext;
        // Intercept to reduce minimum sequence number to the delta manager's minimum sequence number.
        // Sequence numbers are not guaranteed to follow any sort of order. Re-entrancy is one of those situations
        if (this.deltaManager.minimumSequenceNumber <
            messageWithContext.message.minimumSequenceNumber) {
            messageWithContext.message.minimumSequenceNumber =
                this.deltaManager.minimumSequenceNumber;
        }
        // Surround the actual processing of the operation with messages to the schedule manager indicating
        // the beginning and end. This allows it to emit appropriate events and/or pause the processing of new
        // messages once a batch has been fully processed.
        this.scheduleManager.beforeOpProcessing(message);
        this._processedClientSequenceNumber = message.clientSequenceNumber;
        try {
            // RemoteMessageProcessor would have already reconstituted Chunked Ops into the original op type
            assert(message.type !== ContainerMessageType.ChunkedOp, 0x93b /* we should never get here with chunked ops */);
            // If there are no more pending messages after processing a local message,
            // the document is no longer dirty.
            if (!this.hasPendingMessages()) {
                this.updateDocumentDirtyState(false);
            }
            this.validateAndProcessRuntimeMessage(messageWithContext, localOpMetadata);
            this.emit("op", message, messageWithContext.isRuntimeMessage);
            this.scheduleManager.afterOpProcessing(undefined, message);
            if (local) {
                // If we have processed a local op, this means that the container is
                // making progress and we can reset the counter for how many times
                // we have consecutively replayed the pending states
                this.resetReconnectCount();
            }
        }
        catch (e) {
            this.scheduleManager.afterOpProcessing(e, message);
            throw e;
        }
    }
    /**
     * Process an empty batch, which will execute expected actions while processing even if there are no messages.
     * This is a separate function because the processCore function expects at least one message to process.
     * It is expected to happen only when the outbox produces an empty batch due to a resubmit flow.
     */
    processEmptyBatch(emptyBatch, local) {
        const { emptyBatchSequenceNumber: sequenceNumber, batchStartCsn } = emptyBatch;
        assert(sequenceNumber !== undefined, 0x9fa /* emptyBatchSequenceNumber must be defined */);
        this.emit("batchBegin", { sequenceNumber });
        this._processedClientSequenceNumber = batchStartCsn;
        if (!this.hasPendingMessages()) {
            this.updateDocumentDirtyState(false);
        }
        this.emit("batchEnd", undefined, { sequenceNumber });
        if (local) {
            this.resetReconnectCount();
        }
    }
    /**
     * Observes messages that are not intended for the runtime layer, updating/notifying Runtime systems as needed.
     * @param messageWithContext - non-runtime messages to process with additional context and isRuntimeMessage prop as false
     */
    observeNonRuntimeMessage(messageWithContext) {
        const { message, local } = messageWithContext;
        // Intercept to reduce minimum sequence number to the delta manager's minimum sequence number.
        // Sequence numbers are not guaranteed to follow any sort of order. Re-entrancy is one of those situations
        if (this.deltaManager.minimumSequenceNumber <
            messageWithContext.message.minimumSequenceNumber) {
            messageWithContext.message.minimumSequenceNumber =
                this.deltaManager.minimumSequenceNumber;
        }
        // Surround the actual processing of the operation with messages to the schedule manager indicating
        // the beginning and end. This allows it to emit appropriate events and/or pause the processing of new
        // messages once a batch has been fully processed.
        this.scheduleManager.beforeOpProcessing(message);
        this._processedClientSequenceNumber = message.clientSequenceNumber;
        try {
            // If there are no more pending messages after processing a local message,
            // the document is no longer dirty.
            if (!this.hasPendingMessages()) {
                this.updateDocumentDirtyState(false);
            }
            this.emit("op", message, messageWithContext.isRuntimeMessage);
            this.scheduleManager.afterOpProcessing(undefined, message);
            if (local) {
                // If we have processed a local op, this means that the container is
                // making progress and we can reset the counter for how many times
                // we have consecutively replayed the pending states
                this.resetReconnectCount();
            }
        }
        catch (e) {
            this.scheduleManager.afterOpProcessing(e, message);
            throw e;
        }
    }
    /**
     * Assuming the given message is also a TypedContainerRuntimeMessage,
     * checks its type and dispatches the message to the appropriate handler in the runtime.
     * Throws a DataProcessingError if the message looks like but doesn't conform to a known TypedContainerRuntimeMessage type.
     */
    validateAndProcessRuntimeMessage(messageWithContext, localOpMetadata) {
        const { local, message, savedOp } = messageWithContext;
        switch (message.type) {
            case ContainerMessageType.Attach:
            case ContainerMessageType.Alias:
            case ContainerMessageType.FluidDataStoreOp:
                this.channelCollection.process(message, local, localOpMetadata);
                break;
            case ContainerMessageType.BlobAttach:
                this.blobManager.processBlobAttachOp(message, local);
                break;
            case ContainerMessageType.IdAllocation:
                // Don't re-finalize the range if we're processing a "savedOp" in
                // stashed ops flow. The compressor is stashed with these ops already processed.
                // That said, in idCompressorMode === "delayed", we might not serialize ID compressor, and
                // thus we need to process all the ops.
                if (!(this.skipSavedCompressorOps && savedOp === true)) {
                    const range = message.contents;
                    // Some other client turned on the id compressor. If we have not turned it on,
                    // put it in a pending queue and delay finalization.
                    if (this._idCompressor === undefined) {
                        assert(this.idCompressorMode !== undefined, 0x93c /* id compressor should be enabled */);
                        this.pendingIdCompressorOps.push(range);
                    }
                    else {
                        assert(this.pendingIdCompressorOps.length === 0, 0x979 /* there should be no pending ops! */);
                        this._idCompressor.finalizeCreationRange(range);
                    }
                }
                break;
            case ContainerMessageType.GC:
                this.garbageCollector.processMessage(message, message.timestamp, local);
                break;
            case ContainerMessageType.ChunkedOp:
                // From observability POV, we should not exppse the rest of the system (including "op" events on object) to these messages.
                // Also resetReconnectCount() would be wrong - see comment that was there before this change was made.
                assert(false, 0x93d /* should not even get here */);
            case ContainerMessageType.Rejoin:
                break;
            case ContainerMessageType.DocumentSchemaChange:
                this.documentsSchemaController.processDocumentSchemaOp(message.contents, local, message.sequenceNumber);
                break;
            default: {
                const compatBehavior = message.compatDetails?.behavior;
                if (!compatBehaviorAllowsMessageType(message.type, compatBehavior)) {
                    const error = DataProcessingError.create(
                    // Former assert 0x3ce
                    "Runtime message of unknown type", "OpProcessing", message, {
                        local,
                        messageDetails: JSON.stringify({
                            type: message.type,
                            contentType: typeof message.contents,
                            compatBehavior,
                            batch: message.metadata?.batch,
                            compression: message.compression,
                        }),
                    });
                    this.closeFn(error);
                    throw error;
                }
            }
        }
    }
    /**
     * Emits the Signal event and update the perf signal data.
     * @param clientSignalSequenceNumber - is the client signal sequence number to be uploaded.
     */
    sendSignalTelemetryEvent(clientSignalSequenceNumber) {
        const duration = Date.now() - this._perfSignalData.signalTimestamp;
        this.mc.logger.sendPerformanceEvent({
            eventName: "SignalLatency",
            duration,
            signalsLost: this._perfSignalData.signalsLost,
        });
        this._perfSignalData.signalsLost = 0;
        this._perfSignalData.signalTimestamp = 0;
    }
    processSignal(message, local) {
        const envelope = message.content;
        const transformed = {
            clientId: message.clientId,
            content: envelope.contents.content,
            type: envelope.contents.type,
        };
        // Only collect signal telemetry for messages sent by the current client.
        if (message.clientId === this.clientId && this.connected) {
            // Check to see if the signal was lost.
            if (this._perfSignalData.trackingSignalSequenceNumber !== undefined &&
                envelope.clientSignalSequenceNumber > this._perfSignalData.trackingSignalSequenceNumber) {
                this._perfSignalData.signalsLost++;
                this._perfSignalData.trackingSignalSequenceNumber = undefined;
                this.mc.logger.sendErrorEvent({
                    eventName: "SignalLost",
                    type: envelope.contents.type,
                    signalsLost: this._perfSignalData.signalsLost,
                    trackingSequenceNumber: this._perfSignalData.trackingSignalSequenceNumber,
                    clientSignalSequenceNumber: envelope.clientSignalSequenceNumber,
                });
            }
            else if (envelope.clientSignalSequenceNumber ===
                this._perfSignalData.trackingSignalSequenceNumber) {
                // only logging for the first connection and the trackingSignalSequenceNUmber.
                if (this.consecutiveReconnects === 0) {
                    this.sendSignalTelemetryEvent(envelope.clientSignalSequenceNumber);
                }
                this._perfSignalData.trackingSignalSequenceNumber = undefined;
            }
        }
        if (envelope.address === undefined) {
            // No address indicates a container signal message.
            this.emit("signal", transformed, local);
            return;
        }
        // Due to a mismatch between different layers in terms of
        // what is the interface of passing signals, we need to adjust
        // the signal envelope before sending it to the datastores to be processed
        const envelope2 = {
            address: envelope.address,
            contents: transformed.content,
        };
        transformed.content = envelope2;
        this.channelCollection.processSignal(transformed, local);
    }
    /**
     * Flush the pending ops manually.
     * This method is expected to be called at the end of a batch.
     * @param resubmittingBatchId - If defined, indicates this is a resubmission of a batch
     * with the given Batch ID, which must be preserved
     */
    flush(resubmittingBatchId) {
        assert(this._orderSequentiallyCalls === 0, 0x24c /* "Cannot call `flush()` from `orderSequentially`'s callback" */);
        this.outbox.flush(resubmittingBatchId);
        assert(this.outbox.isEmpty, 0x3cf /* reentrancy */);
    }
    /**
     * {@inheritDoc @fluidframework/runtime-definitions#IContainerRuntimeBase.orderSequentially}
     */
    orderSequentially(callback) {
        let checkpoint;
        let result;
        if (this.mc.config.getBoolean("Fluid.ContainerRuntime.EnableRollback")) {
            // Note: we are not touching any batches other than mainBatch here, for two reasons:
            // 1. It would not help, as other batches are flushed independently from main batch.
            // 2. There is no way to undo process of data store creation, blob creation, ID compressor ops, or other things tracked by other batches.
            checkpoint = this.outbox.getBatchCheckpoints().mainBatch;
        }
        try {
            this._orderSequentiallyCalls++;
            result = callback();
        }
        catch (error) {
            if (checkpoint) {
                // This will throw and close the container if rollback fails
                try {
                    checkpoint.rollback((message) => this.rollback(message.contents, message.localOpMetadata));
                }
                catch (err) {
                    const error2 = wrapError(err, (message) => {
                        return DataProcessingError.create(`RollbackError: ${message}`, "checkpointRollback", undefined);
                    });
                    this.closeFn(error2);
                    throw error2;
                }
            }
            else {
                this.closeFn(wrapError(error, (errorMessage) => new GenericError(`orderSequentially callback exception: ${errorMessage}`, error, {
                    orderSequentiallyCalls: this._orderSequentiallyCalls,
                })));
            }
            throw error; // throw the original error for the consumer of the runtime
        }
        finally {
            this._orderSequentiallyCalls--;
        }
        // We don't flush on TurnBased since we expect all messages in the same JS turn to be part of the same batch
        if (this.flushMode !== FlushMode.TurnBased && this._orderSequentiallyCalls === 0) {
            this.flush();
        }
        return result;
    }
    /**
     * Returns the aliased data store's entryPoint, given the alias.
     * @param alias - The alias for the data store.
     * @returns The data store's entry point ({@link @fluidframework/core-interfaces#IFluidHandle}) if it exists and is aliased.
     * Returns undefined if no data store has been assigned the given alias.
     */
    async getAliasedDataStoreEntryPoint(alias) {
        // Back-comapatibility:
        // There are old files that were created without using data store aliasing feature, but
        // used createRoot*DataStore*() (already removed) API. Such data stores will have isRoot = true,
        // and internalID provided by user. The expectation is that such files behave as new files, where
        // same data store instances created using aliasing feature.
        // Please also see note on name collisions in DataStores.createDataStoreId()
        await this.channelCollection.waitIfPendingAlias(alias);
        const internalId = this.internalId(alias);
        const context = await this.channelCollection.getDataStoreIfAvailable(internalId, {
            wait: false,
        });
        // If the data store is not available or not an alias, return undefined.
        if (context === undefined || !(await context.isRoot())) {
            return undefined;
        }
        const channel = await context.realize();
        if (channel.entryPoint === undefined) {
            throw new UsageError("entryPoint must be defined on data store runtime for using getAliasedDataStoreEntryPoint");
        }
        this.garbageCollector.nodeUpdated({
            node: { type: "DataStore", path: `/${internalId}` },
            reason: "Loaded",
            packagePath: context.packagePath,
            timestampMs: this.getCurrentReferenceTimestampMs(),
        });
        return channel.entryPoint;
    }
    createDetachedDataStore(pkg, loadingGroupId) {
        return this.channelCollection.createDetachedDataStore(pkg, loadingGroupId);
    }
    async createDataStore(pkg, loadingGroupId) {
        const context = this.channelCollection.createDataStoreContext(Array.isArray(pkg) ? pkg : [pkg], undefined, // props
        loadingGroupId);
        return channelToDataStore(await context.realize(), context.id, this.channelCollection, this.mc.logger);
    }
    /**
     * @deprecated 0.16 Issue #1537, #3631
     */
    async _createDataStoreWithProps(pkg, props) {
        const context = this.channelCollection.createDataStoreContext(Array.isArray(pkg) ? pkg : [pkg], props);
        return channelToDataStore(await context.realize(), context.id, this.channelCollection, this.mc.logger);
    }
    canSendOps() {
        // Note that the real (non-proxy) delta manager is needed here to get the readonly info. This is because
        // container runtime's ability to send ops depend on the actual readonly state of the delta manager.
        return (this.connected && !this.innerDeltaManager.readOnlyInfo.readonly && !this.imminentClosure);
    }
    /**
     * Typically ops are batched and later flushed together, but in some cases we want to flush immediately.
     */
    currentlyBatching() {
        return this.flushMode !== FlushMode.Immediate || this._orderSequentiallyCalls !== 0;
    }
    getQuorum() {
        return this._quorum;
    }
    getAudience() {
        return this._audience;
    }
    /**
     * Returns true of container is dirty, i.e. there are some pending local changes that
     * either were not sent out to delta stream or were not yet acknowledged.
     */
    get isDirty() {
        return this.dirtyContainer;
    }
    isContainerMessageDirtyable({ type, contents }) {
        // Certain container runtime messages should not mark the container dirty such as the old built-in
        // AgentScheduler and Garbage collector messages.
        switch (type) {
            case ContainerMessageType.Attach: {
                const attachMessage = contents;
                if (attachMessage.id === agentSchedulerId) {
                    return false;
                }
                break;
            }
            case ContainerMessageType.FluidDataStoreOp: {
                const envelope = contents;
                if (envelope.address === agentSchedulerId) {
                    return false;
                }
                break;
            }
            case ContainerMessageType.IdAllocation:
            case ContainerMessageType.DocumentSchemaChange:
            case ContainerMessageType.GC: {
                return false;
            }
            default:
                break;
        }
        return true;
    }
    createNewSignalEnvelope(address, type, content) {
        const newSequenceNumber = ++this._perfSignalData.signalSequenceNumber;
        const newEnvelope = {
            address,
            clientSignalSequenceNumber: newSequenceNumber,
            contents: { type, content },
        };
        // We should not track any signals in case we already have a tracking number.
        if (newSequenceNumber % this.defaultTelemetrySignalSampleCount === 1 &&
            this._perfSignalData.trackingSignalSequenceNumber === undefined) {
            this._perfSignalData.signalTimestamp = Date.now();
            this._perfSignalData.trackingSignalSequenceNumber = newSequenceNumber;
        }
        return newEnvelope;
    }
    /**
     * Submits the signal to be sent to other clients.
     * @param type - Type of the signal.
     * @param content - Content of the signal. Should be a JSON serializable object or primitive.
     * @param targetClientId - When specified, the signal is only sent to the provided client id.
     */
    submitSignal(type, content, targetClientId) {
        this.verifyNotClosed();
        const envelope = this.createNewSignalEnvelope(undefined /* address */, type, content);
        return this.submitSignalFn(envelope, targetClientId);
    }
    setAttachState(attachState) {
        if (attachState === AttachState.Attaching) {
            assert(this.attachState === AttachState.Attaching, 0x12d /* "Container Context should already be in attaching state" */);
        }
        else {
            assert(this.attachState === AttachState.Attached, 0x12e /* "Container Context should already be in attached state" */);
            this.emit("attached");
        }
        if (attachState === AttachState.Attached && !this.hasPendingMessages()) {
            this.updateDocumentDirtyState(false);
        }
        this.channelCollection.setAttachState(attachState);
    }
    /**
     * Create a summary. Used when attaching or serializing a detached container.
     *
     * @param blobRedirectTable - A table passed during the attach process. While detached, blob upload is supported
     * using IDs generated locally. After attach, these IDs cannot be used, so this table maps the old local IDs to the
     * new storage IDs so requests can be redirected.
     * @param telemetryContext - summary data passed through the layers for telemetry purposes
     */
    createSummary(blobRedirectTable, telemetryContext) {
        if (blobRedirectTable) {
            this.blobManager.setRedirectTable(blobRedirectTable);
        }
        // We can finalize any allocated IDs since we're the only client
        const idRange = this._idCompressor?.takeNextCreationRange();
        if (idRange !== undefined) {
            assert(idRange.ids === undefined || idRange.ids.firstGenCount === 1, 0x93e /* No other ranges should be taken while container is detached. */);
            this._idCompressor?.finalizeCreationRange(idRange);
        }
        const summarizeResult = this.channelCollection.getAttachSummary(telemetryContext);
        // Wrap data store summaries in .channels subtree.
        wrapSummaryInChannelsTree(summarizeResult);
        this.addContainerStateToSummary(summarizeResult, true /* fullTree */, false /* trackState */, telemetryContext);
        return summarizeResult.summary;
    }
    async summarizeInternal(fullTree, trackState, telemetryContext) {
        const summarizeResult = await this.channelCollection.summarize(fullTree, trackState, telemetryContext);
        // Wrap data store summaries in .channels subtree.
        wrapSummaryInChannelsTree(summarizeResult);
        const pathPartsForChildren = [channelsTreeName];
        // Ensure that ID compressor had a chance to load, if we are using delayed mode.
        await this.loadIdCompressor();
        this.addContainerStateToSummary(summarizeResult, fullTree, trackState, telemetryContext);
        return {
            ...summarizeResult,
            id: "",
            pathPartsForChildren,
        };
    }
    /**
     * Returns a summary of the runtime at the current sequence number.
     */
    async summarize(options) {
        this.verifyNotClosed();
        const { fullTree = false, trackState = true, summaryLogger = this.mc.logger, runGC = this.garbageCollector.shouldRunGC, runSweep, fullGC, } = options;
        const telemetryContext = new TelemetryContext();
        // Add the options that are used to generate this summary to the telemetry context.
        telemetryContext.setMultiple("fluid_Summarize", "Options", {
            fullTree,
            trackState,
            runGC,
            fullGC,
            runSweep,
        });
        try {
            if (runGC) {
                await this.collectGarbage({ logger: summaryLogger, runSweep, fullGC }, telemetryContext);
            }
            const { stats, summary } = await this.summarizerNode.summarize(fullTree, trackState, telemetryContext);
            assert(summary.type === SummaryType.Tree, 0x12f /* "Container Runtime's summarize should always return a tree" */);
            return { stats, summary };
        }
        finally {
            summaryLogger.sendTelemetryEvent({
                eventName: "SummarizeTelemetry",
                details: telemetryContext.serialize(),
            });
        }
    }
    async getGCDataInternal(fullGC) {
        return this.channelCollection.getGCData(fullGC);
    }
    /**
     * Generates and returns the GC data for this container.
     * @param fullGC - true to bypass optimizations and force full generation of GC data.
     * @see IGarbageCollectionRuntime.getGCData
     */
    async getGCData(fullGC) {
        const builder = new GCDataBuilder();
        const dsGCData = await this.summarizerNode.getGCData(fullGC);
        builder.addNodes(dsGCData.gcNodes);
        const blobsGCData = this.blobManager.getGCData(fullGC);
        builder.addNodes(blobsGCData.gcNodes);
        return builder.getGCData();
    }
    /**
     * After GC has run, called to notify this container's nodes of routes that are used in it.
     * @param usedRoutes - The routes that are used in all nodes in this Container.
     * @see IGarbageCollectionRuntime.updateUsedRoutes
     */
    updateUsedRoutes(usedRoutes) {
        // Update our summarizer node's used routes. Updating used routes in summarizer node before
        // summarizing is required and asserted by the the summarizer node. We are the root and are
        // always referenced, so the used routes is only self-route (empty string).
        this.summarizerNode.updateUsedRoutes([""]);
        const { dataStoreRoutes } = this.getDataStoreAndBlobManagerRoutes(usedRoutes);
        this.channelCollection.updateUsedRoutes(dataStoreRoutes);
    }
    /**
     * After GC has run and identified nodes that are sweep ready, this is called to delete the sweep ready nodes.
     * @param sweepReadyRoutes - The routes of nodes that are sweep ready and should be deleted.
     * @returns The routes of nodes that were deleted.
     */
    deleteSweepReadyNodes(sweepReadyRoutes) {
        const { dataStoreRoutes, blobManagerRoutes } = this.getDataStoreAndBlobManagerRoutes(sweepReadyRoutes);
        const deletedRoutes = this.channelCollection.deleteSweepReadyNodes(dataStoreRoutes);
        return deletedRoutes.concat(this.blobManager.deleteSweepReadyNodes(blobManagerRoutes));
    }
    /**
     * This is called to update objects that are tombstones.
     *
     * A Tombstoned object has been unreferenced long enough that GC knows it won't be referenced again.
     * Tombstoned objects are eventually deleted by GC.
     *
     * @param tombstonedRoutes - Data store and attachment blob routes that are tombstones in this Container.
     */
    updateTombstonedRoutes(tombstonedRoutes) {
        const { dataStoreRoutes } = this.getDataStoreAndBlobManagerRoutes(tombstonedRoutes);
        this.channelCollection.updateTombstonedRoutes(dataStoreRoutes);
    }
    /**
     * Returns a server generated referenced timestamp to be used to track unreferenced nodes by GC.
     */
    getCurrentReferenceTimestampMs() {
        // Use the timestamp of the last message seen by this client as that is server generated. If no messages have
        // been processed, use the timestamp of the message from the last summary.
        return this.deltaManager.lastMessage?.timestamp ?? this.messageAtLastSummary?.timestamp;
    }
    /**
     * Returns the type of the GC node. Currently, there are nodes that belong to the root ("/"), data stores or
     * blob manager.
     */
    getNodeType(nodePath) {
        if (isBlobPath(nodePath)) {
            return GCNodeType.Blob;
        }
        return this.channelCollection.getGCNodeType(nodePath) ?? GCNodeType.Other;
    }
    /**
     * Called by GC to retrieve the package path of the node with the given path. The node should belong to a
     * data store or an attachment blob.
     */
    async getGCNodePackagePath(nodePath) {
        // GC uses "/" when adding "root" references, e.g. for Aliasing or as part of Tombstone Auto-Recovery.
        // These have no package path so return a special value.
        if (nodePath === "/") {
            return ["_gcRoot"];
        }
        switch (this.getNodeType(nodePath)) {
            case GCNodeType.Blob:
                return [blobManagerBasePath];
            case GCNodeType.DataStore:
            case GCNodeType.SubDataStore:
                return this.channelCollection.getDataStorePackagePath(nodePath);
            default:
                assert(false, 0x2de /* "Package path requested for unsupported node type." */);
        }
    }
    /**
     * From a given list of routes, separate and return routes that belong to blob manager and data stores.
     * @param routes - A list of routes that can belong to data stores or blob manager.
     * @returns Two route lists - One that contains routes for blob manager and another one that contains routes
     * for data stores.
     */
    getDataStoreAndBlobManagerRoutes(routes) {
        const blobManagerRoutes = [];
        const dataStoreRoutes = [];
        for (const route of routes) {
            if (isBlobPath(route)) {
                blobManagerRoutes.push(route);
            }
            else {
                dataStoreRoutes.push(route);
            }
        }
        return { blobManagerRoutes, dataStoreRoutes };
    }
    /**
     * Runs garbage collection and updates the reference / used state of the nodes in the container.
     * @returns the statistics of the garbage collection run; undefined if GC did not run.
     */
    async collectGarbage(options, telemetryContext) {
        return this.garbageCollector.collectGarbage(options, telemetryContext);
    }
    /**
     * Called when a new outbound route is added to another node. This is used by garbage collection to identify
     * all references added in the system.
     * @param fromPath - The absolute path of the node that added the reference.
     * @param toPath - The absolute path of the outbound node that is referenced.
     * @param messageTimestampMs - The timestamp of the message that added the reference.
     */
    addedGCOutboundRoute(fromPath, toPath, messageTimestampMs) {
        // This is always called when processing an op so messageTimestampMs should exist. Due to back-compat
        // across the data store runtime / container runtime boundary, this may be undefined and if so, get
        // the timestamp from the last processed message which should exist.
        // If a timestamp doesn't exist, log so we can learn about these cases and return.
        const timestampMs = messageTimestampMs ?? this.getCurrentReferenceTimestampMs();
        if (timestampMs === undefined) {
            this.mc.logger.sendTelemetryEvent({
                eventName: "NoTimestampInGCOutboundRoute",
                ...tagCodeArtifacts({
                    id: toPath,
                    fromId: fromPath,
                }),
            });
            return;
        }
        this.garbageCollector.addedOutboundReference(fromPath, toPath, timestampMs);
    }
    /**
     * Generates the summary tree, uploads it to storage, and then submits the summarize op.
     * This is intended to be called by the summarizer, since it is the implementation of
     * ISummarizerInternalsProvider.submitSummary.
     * It takes care of state management at the container level, including pausing inbound
     * op processing, updating SummarizerNode state tracking, and garbage collection.
     * @param options - options controlling how the summary is generated or submitted
     */
    async submitSummary(options) {
        const { fullTree = false, finalAttempt = false, summaryLogger, latestSummaryRefSeqNum, } = options;
        // The summary number for this summary. This will be updated during the summary process, so get it now and
        // use it for all events logged during this summary.
        const summaryNumber = this.nextSummaryNumber;
        let summaryRefSeqNum;
        const summaryNumberLogger = createChildLogger({
            logger: summaryLogger,
            properties: {
                all: {
                    summaryNumber,
                    referenceSequenceNumber: () => summaryRefSeqNum,
                },
            },
        });
        assert(this.outbox.isEmpty, 0x3d1 /* Can't trigger summary in the middle of a batch */);
        // If the container is dirty, i.e., there are pending unacked ops, the summary will not be eventual consistent
        // and it may even be incorrect. So, wait for the container to be saved with a timeout. If the container is not
        // saved within the timeout, check if it should be failed or can continue.
        if (this.isDirty) {
            const countBefore = this.pendingMessagesCount;
            // The timeout for waiting for pending ops can be overridden via configurations.
            const pendingOpsTimeout = this.mc.config.getNumber("Fluid.Summarizer.waitForPendingOpsTimeoutMs") ??
                defaultPendingOpsWaitTimeoutMs;
            await new Promise((resolve, reject) => {
                const timeoutId = setTimeout(() => resolve(), pendingOpsTimeout);
                this.once("saved", () => {
                    clearTimeout(timeoutId);
                    resolve();
                });
                this.once("dispose", () => {
                    clearTimeout(timeoutId);
                    reject(new Error("Runtime is disposed while summarizing"));
                });
            });
            // Log that there are pending ops while summarizing. This will help us gather data on how often this
            // happens, whether we attempted to wait for these ops to be acked and what was the result.
            summaryNumberLogger.sendTelemetryEvent({
                eventName: "PendingOpsWhileSummarizing",
                saved: !this.isDirty,
                timeout: pendingOpsTimeout,
                countBefore,
                countAfter: this.pendingMessagesCount,
            });
            // There could still be pending ops. Check if summary should fail or continue.
            const pendingMessagesFailResult = await this.shouldFailSummaryOnPendingOps(summaryNumberLogger, this.deltaManager.lastSequenceNumber, this.deltaManager.minimumSequenceNumber, finalAttempt, true /* beforeSummaryGeneration */);
            if (pendingMessagesFailResult !== undefined) {
                return pendingMessagesFailResult;
            }
        }
        const shouldPauseInboundSignal = this.mc.config.getBoolean("Fluid.ContainerRuntime.SubmitSummary.disableInboundSignalPause") !== true;
        const shouldValidatePreSummaryState = this.mc.config.getBoolean("Fluid.ContainerRuntime.SubmitSummary.shouldValidatePreSummaryState") === true;
        try {
            await this.deltaManager.inbound.pause();
            if (shouldPauseInboundSignal) {
                await this.deltaManager.inboundSignal.pause();
            }
            summaryRefSeqNum = this.deltaManager.lastSequenceNumber;
            const minimumSequenceNumber = this.deltaManager.minimumSequenceNumber;
            const message = `Summary @${summaryRefSeqNum}:${this.deltaManager.minimumSequenceNumber}`;
            const lastAckedContext = this.lastAckedSummaryContext;
            const startSummaryResult = this.summarizerNode.startSummary(summaryRefSeqNum, summaryNumberLogger, latestSummaryRefSeqNum);
            /**
             * This was added to validate that the summarizer node tree has the same reference sequence number from the
             * top running summarizer down to the lowest summarizer node.
             *
             * The order of mismatch numbers goes (validate sequence number)-(node sequence number).
             * Generally the validate sequence number comes from the running summarizer and the node sequence number comes from the
             * summarizer nodes.
             */
            if (startSummaryResult.invalidNodes > 0 || startSummaryResult.mismatchNumbers.size > 0) {
                summaryLogger.sendTelemetryEvent({
                    eventName: "LatestSummaryRefSeqNumMismatch",
                    details: {
                        ...startSummaryResult,
                        mismatchNumbers: Array.from(startSummaryResult.mismatchNumbers),
                    },
                });
                if (shouldValidatePreSummaryState && !finalAttempt) {
                    return {
                        stage: "base",
                        referenceSequenceNumber: summaryRefSeqNum,
                        minimumSequenceNumber,
                        error: new RetriableSummaryError(`Summarizer node state inconsistent with summarizer state.`),
                    };
                }
            }
            // Helper function to check whether we should still continue between each async step.
            const checkContinue = () => {
                // Do not check for loss of connectivity directly! Instead leave it up to
                // RunWhileConnectedCoordinator to control policy in a single place.
                // This will allow easier change of design if we chose to. For example, we may chose to allow
                // summarizer to reconnect in the future.
                // Also checking for cancellation is a must as summary process may be abandoned for other reasons,
                // like loss of connectivity for main (interactive) client.
                if (options.cancellationToken.cancelled) {
                    return { continue: false, error: "disconnected" };
                }
                // That said, we rely on submitSystemMessage() that today only works in connected state.
                // So if we fail here, it either means that RunWhileConnectedCoordinator does not work correctly,
                // OR that design changed and we need to remove this check and fix submitSystemMessage.
                assert(this.connected, 0x258 /* "connected" */);
                // Ensure that lastSequenceNumber has not changed after pausing.
                // We need the summary op's reference sequence number to match our summary sequence number,
                // otherwise we'll get the wrong sequence number stamped on the summary's .protocol attributes.
                if (this.deltaManager.lastSequenceNumber !== summaryRefSeqNum) {
                    return {
                        continue: false,
                        error: `lastSequenceNumber changed before uploading to storage. ${this.deltaManager.lastSequenceNumber} !== ${summaryRefSeqNum}`,
                    };
                }
                assert(summaryRefSeqNum === this.deltaManager.lastMessage?.sequenceNumber, 0x395 /* it's one and the same thing */);
                if (lastAckedContext !== this.lastAckedSummaryContext) {
                    return {
                        continue: false,
                        error: `Last summary changed while summarizing. ${this.lastAckedSummaryContext} !== ${lastAckedContext}`,
                    };
                }
                return { continue: true };
            };
            let continueResult = checkContinue();
            if (!continueResult.continue) {
                return {
                    stage: "base",
                    referenceSequenceNumber: summaryRefSeqNum,
                    minimumSequenceNumber,
                    error: new RetriableSummaryError(continueResult.error),
                };
            }
            const trace = Trace.start();
            let summarizeResult;
            try {
                summarizeResult = await this.summarize({
                    fullTree,
                    trackState: true,
                    summaryLogger: summaryNumberLogger,
                    runGC: this.garbageCollector.shouldRunGC,
                });
            }
            catch (error) {
                return {
                    stage: "base",
                    referenceSequenceNumber: summaryRefSeqNum,
                    minimumSequenceNumber,
                    error: wrapError(error, (msg) => new RetriableSummaryError(msg)),
                };
            }
            // Validate that the summary generated by summarizer nodes is correct before uploading.
            const validateResult = this.summarizerNode.validateSummary();
            if (!validateResult.success) {
                const { success, ...loggingProps } = validateResult;
                const error = new RetriableSummaryError(validateResult.reason, validateResult.retryAfterSeconds, { ...loggingProps });
                return {
                    stage: "base",
                    referenceSequenceNumber: summaryRefSeqNum,
                    minimumSequenceNumber,
                    error,
                };
            }
            // If there are pending unacked ops, this summary attempt may fail as the uploaded
            // summary would be eventually inconsistent.
            const pendingMessagesFailResult = await this.shouldFailSummaryOnPendingOps(summaryNumberLogger, summaryRefSeqNum, minimumSequenceNumber, finalAttempt, false /* beforeSummaryGeneration */);
            if (pendingMessagesFailResult !== undefined) {
                return pendingMessagesFailResult;
            }
            const { summary: summaryTree, stats: partialStats } = summarizeResult;
            // Now that we have generated the summary, update the message at last summary to the last message processed.
            this.messageAtLastSummary = this.deltaManager.lastMessage;
            // Counting dataStores and handles
            // Because handles are unchanged dataStores in the current logic,
            // summarized dataStore count is total dataStore count minus handle count
            // TODO why are we non null asserting here
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const dataStoreTree = summaryTree.tree[channelsTreeName];
            assert(dataStoreTree.type === SummaryType.Tree, 0x1fc /* "summary is not a tree" */);
            const handleCount = Object.values(dataStoreTree.tree).filter((value) => value.type === SummaryType.Handle).length;
            const gcSummaryTreeStats = summaryTree.tree[gcTreeKey]
                ? calculateStats(summaryTree.tree[gcTreeKey])
                : undefined;
            const summaryStats = {
                dataStoreCount: this.channelCollection.size,
                summarizedDataStoreCount: this.channelCollection.size - handleCount,
                gcStateUpdatedDataStoreCount: this.garbageCollector.updatedDSCountSinceLastSummary,
                gcBlobNodeCount: gcSummaryTreeStats?.blobNodeCount,
                gcTotalBlobsSize: gcSummaryTreeStats?.totalBlobSize,
                summaryNumber,
                ...partialStats,
            };
            const generateSummaryData = {
                referenceSequenceNumber: summaryRefSeqNum,
                minimumSequenceNumber,
                summaryTree,
                summaryStats,
                generateDuration: trace.trace().duration,
            };
            continueResult = checkContinue();
            if (!continueResult.continue) {
                return {
                    stage: "generate",
                    ...generateSummaryData,
                    error: new RetriableSummaryError(continueResult.error),
                };
            }
            const summaryContext = {
                proposalHandle: this.lastAckedSummaryContext?.proposalHandle ?? undefined,
                ackHandle: this.lastAckedSummaryContext?.ackHandle ?? this.loadedFromVersionId,
                referenceSequenceNumber: summaryRefSeqNum,
            };
            let handle;
            try {
                handle = await this.storage.uploadSummaryWithContext(summarizeResult.summary, summaryContext);
            }
            catch (error) {
                return {
                    stage: "generate",
                    ...generateSummaryData,
                    error: wrapError(error, (msg) => new RetriableSummaryError(msg)),
                };
            }
            const parent = summaryContext.ackHandle;
            const summaryMessage = {
                handle,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                head: parent,
                message,
                parents: parent ? [parent] : [],
            };
            const uploadData = {
                ...generateSummaryData,
                handle,
                uploadDuration: trace.trace().duration,
            };
            continueResult = checkContinue();
            if (!continueResult.continue) {
                return {
                    stage: "upload",
                    ...uploadData,
                    error: new RetriableSummaryError(continueResult.error),
                };
            }
            let clientSequenceNumber;
            try {
                clientSequenceNumber = this.submitSummaryMessage(summaryMessage, summaryRefSeqNum);
            }
            catch (error) {
                return {
                    stage: "upload",
                    ...uploadData,
                    error: wrapError(error, (msg) => new RetriableSummaryError(msg)),
                };
            }
            const submitData = {
                stage: "submit",
                ...uploadData,
                clientSequenceNumber,
                submitOpDuration: trace.trace().duration,
            };
            try {
                this.summarizerNode.completeSummary(handle);
            }
            catch (error) {
                return {
                    stage: "upload",
                    ...uploadData,
                    error: wrapError(error, (msg) => new RetriableSummaryError(msg)),
                };
            }
            return submitData;
        }
        finally {
            // Cleanup wip summary in case of failure
            this.summarizerNode.clearSummary();
            // ! This needs to happen before we resume inbound queues to ensure heuristics are tracked correctly
            this._summarizer?.recordSummaryAttempt?.(summaryRefSeqNum);
            // Restart the delta manager
            this.deltaManager.inbound.resume();
            if (shouldPauseInboundSignal) {
                this.deltaManager.inboundSignal.resume();
            }
        }
    }
    /**
     * This helper is called during summarization. If the container is dirty, it will return a failed summarize result
     * (IBaseSummarizeResult) unless this is the final summarize attempt and SkipFailingIncorrectSummary option is set.
     * @param logger - The logger to be used for sending telemetry.
     * @param referenceSequenceNumber - The reference sequence number of the summary attempt.
     * @param minimumSequenceNumber - The minimum sequence number of the summary attempt.
     * @param finalAttempt - Whether this is the final summary attempt.
     * @param beforeSummaryGeneration - Whether this is called before summary generation or after.
     * @returns failed summarize result (IBaseSummarizeResult) if summary should be failed, undefined otherwise.
     */
    async shouldFailSummaryOnPendingOps(logger, referenceSequenceNumber, minimumSequenceNumber, finalAttempt, beforeSummaryGeneration) {
        if (!this.isDirty) {
            return;
        }
        // If "SkipFailingIncorrectSummary" option is true, don't fail the summary in the last attempt.
        // This is a fallback to make progress in documents where there are consistently pending ops in
        // the summarizer.
        if (finalAttempt &&
            this.mc.config.getBoolean("Fluid.Summarizer.SkipFailingIncorrectSummary")) {
            const error = DataProcessingError.create("Pending ops during summarization", "submitSummary", undefined, { pendingMessages: this.pendingMessagesCount });
            logger.sendErrorEvent({
                eventName: "SkipFailingIncorrectSummary",
                referenceSequenceNumber,
                minimumSequenceNumber,
                beforeGenerate: beforeSummaryGeneration,
            }, error);
        }
        else {
            // The retry delay when there are pending ops can be overridden via config so that we can adjust it
            // based on telemetry while we decide on a stable number.
            const retryDelayMs = this.mc.config.getNumber("Fluid.Summarizer.PendingOpsRetryDelayMs") ??
                defaultPendingOpsRetryDelayMs;
            const error = new RetriableSummaryError("PendingOpsWhileSummarizing", retryDelayMs / 1000, {
                count: this.pendingMessagesCount,
                beforeGenerate: beforeSummaryGeneration,
            });
            return {
                stage: "base",
                referenceSequenceNumber,
                minimumSequenceNumber,
                error,
            };
        }
    }
    get pendingMessagesCount() {
        return this.pendingStateManager.pendingMessagesCount + this.outbox.messageCount;
    }
    hasPendingMessages() {
        return this.pendingMessagesCount !== 0;
    }
    updateDocumentDirtyState(dirty) {
        if (this.attachState !== AttachState.Attached) {
            assert(dirty, 0x3d2 /* Non-attached container is dirty */);
        }
        else {
            // Other way is not true = see this.isContainerMessageDirtyable()
            assert(!dirty || this.hasPendingMessages(), 0x3d3 /* if doc is dirty, there has to be pending ops */);
        }
        if (this.dirtyContainer === dirty) {
            return;
        }
        this.dirtyContainer = dirty;
        if (this.emitDirtyDocumentEvent) {
            this.emit(dirty ? "dirty" : "saved");
        }
    }
    submitMessage(type, contents, localOpMetadata = undefined) {
        this.submit({ type, contents }, localOpMetadata);
    }
    async uploadBlob(blob, signal) {
        this.verifyNotClosed();
        return this.blobManager.createBlob(blob, signal);
    }
    submitIdAllocationOpIfNeeded(resubmitOutstandingRanges) {
        if (this._idCompressor) {
            const idRange = resubmitOutstandingRanges
                ? this._idCompressor.takeUnfinalizedCreationRange()
                : this._idCompressor.takeNextCreationRange();
            // Don't include the idRange if there weren't any Ids allocated
            if (idRange.ids !== undefined) {
                const idAllocationMessage = {
                    type: ContainerMessageType.IdAllocation,
                    contents: idRange,
                };
                const idAllocationBatchMessage = {
                    contents: JSON.stringify(idAllocationMessage),
                    referenceSequenceNumber: this.deltaManager.lastSequenceNumber,
                };
                this.outbox.submitIdAllocation(idAllocationBatchMessage);
            }
        }
    }
    submit(containerRuntimeMessage, localOpMetadata = undefined, metadata) {
        this.verifyNotClosed();
        // There should be no ops in detached container state!
        assert(this.attachState !== AttachState.Detached, 0x132 /* "sending ops in detached container" */);
        assert(metadata === undefined ||
            containerRuntimeMessage.type === ContainerMessageType.BlobAttach, 0x93f /* metadata */);
        // Note that the real (non-proxy) delta manager is used here to get the readonly info. This is because
        // container runtime's ability to submit ops depend on the actual readonly state of the delta manager.
        if (this.innerDeltaManager.readOnlyInfo.readonly) {
            this.mc.logger.sendTelemetryEvent({
                eventName: "SubmitOpInReadonly",
                connected: this.connected,
            });
        }
        const type = containerRuntimeMessage.type;
        assert(type !== ContainerMessageType.IdAllocation, 0x9a5 /* IdAllocation should be submitted directly to outbox. */);
        try {
            this.submitIdAllocationOpIfNeeded(false);
            // Allow document schema controller to send a message if it needs to propose change in document schema.
            // If it needs to send a message, it will call provided callback with payload of such message and rely
            // on this callback to do actual sending.
            const schemaChangeMessage = this.documentsSchemaController.maybeSendSchemaMessage();
            if (schemaChangeMessage) {
                this.logger.sendTelemetryEvent({
                    eventName: "SchemaChangeProposal",
                    refSeq: schemaChangeMessage.refSeq,
                    version: schemaChangeMessage.version,
                    newRuntimeSchema: JSON.stringify(schemaChangeMessage.runtime),
                    sessionRuntimeSchema: JSON.stringify(this.sessionSchema),
                    oldRuntimeSchema: JSON.stringify(this.metadata?.documentSchema?.runtime),
                });
                const msg = {
                    type: ContainerMessageType.DocumentSchemaChange,
                    contents: schemaChangeMessage,
                };
                this.outbox.submit({
                    contents: JSON.stringify(msg),
                    referenceSequenceNumber: this.deltaManager.lastSequenceNumber,
                });
            }
            const message = {
                contents: JSON.stringify(containerRuntimeMessage) /* serialized content */,
                metadata,
                localOpMetadata,
                referenceSequenceNumber: this.deltaManager.lastSequenceNumber,
            };
            if (type === ContainerMessageType.BlobAttach) {
                // BlobAttach ops must have their metadata visible and cannot be grouped (see opGroupingManager.ts)
                this.outbox.submitBlobAttach(message);
            }
            else {
                this.outbox.submit(message);
            }
            // Note: Technically, the system "always" batches - if this case is true we'll just have a single-message batch.
            const flushImmediatelyOnSubmit = !this.currentlyBatching();
            if (flushImmediatelyOnSubmit) {
                this.flush();
            }
            else {
                this.scheduleFlush();
            }
        }
        catch (error) {
            this.closeFn(error);
            throw error;
        }
        if (this.isContainerMessageDirtyable(containerRuntimeMessage)) {
            this.updateDocumentDirtyState(true);
        }
    }
    scheduleFlush() {
        if (this.flushTaskExists) {
            return;
        }
        this.flushTaskExists = true;
        const flush = () => {
            this.flushTaskExists = false;
            try {
                this.flush();
            }
            catch (error) {
                this.closeFn(error);
            }
        };
        switch (this.flushMode) {
            case FlushMode.TurnBased:
                // When in TurnBased flush mode the runtime will buffer operations in the current turn and send them as a single
                // batch at the end of the turn
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                Promise.resolve().then(flush);
                break;
            // FlushModeExperimental is experimental and not exposed directly in the runtime APIs
            case FlushModeExperimental.Async:
                // When in Async flush mode, the runtime will accumulate all operations across JS turns and send them as a single
                // batch when all micro-tasks are complete.
                // Compared to TurnBased, this flush mode will capture more ops into the same batch.
                setTimeout(flush, 0);
                break;
            default:
                assert(this._orderSequentiallyCalls > 0, 0x587 /* Unreachable unless running under orderSequentially */);
                break;
        }
    }
    submitSummaryMessage(contents, referenceSequenceNumber) {
        this.verifyNotClosed();
        assert(this.connected, 0x133 /* "Container disconnected when trying to submit system message" */);
        // System message should not be sent in the middle of the batch.
        assert(this.outbox.isEmpty, 0x3d4 /* System op in the middle of a batch */);
        // back-compat: ADO #1385: Make this call unconditional in the future
        return this.submitSummaryFn !== undefined
            ? this.submitSummaryFn(contents, referenceSequenceNumber)
            : this.submitFn(MessageType.Summarize, contents, false);
    }
    /**
     * Throw an error if the runtime is closed.  Methods that are expected to potentially
     * be called after dispose due to asynchrony should not call this.
     */
    verifyNotClosed() {
        if (this._disposed) {
            throw new Error("Runtime is closed");
        }
    }
    reSubmitBatch(batch, batchId) {
        this.orderSequentially(() => {
            for (const message of batch) {
                this.reSubmit(message);
            }
        });
        // Only include Batch ID if "Offline Load" feature is enabled
        // It's only needed to identify batches across container forks arising from misuse of offline load.
        this.flush(this.offlineEnabled ? batchId : undefined);
    }
    reSubmit(message) {
        // Need to parse from string for back-compat
        const containerRuntimeMessage = this.parseLocalOpContent(message.content);
        this.reSubmitCore(containerRuntimeMessage, message.localOpMetadata, message.opMetadata);
    }
    /**
     * Finds the right store and asks it to resubmit the message. This typically happens when we
     * reconnect and there are pending messages.
     * ! Note: successfully resubmitting an op that has been successfully sequenced is not possible due to checks in the ConnectionStateHandler (Loader layer)
     * @param message - The original LocalContainerRuntimeMessage.
     * @param localOpMetadata - The local metadata associated with the original message.
     */
    reSubmitCore(message, localOpMetadata, opMetadata) {
        assert(!this.isSummarizerClient, 0x8f2 /* Summarizer never reconnects so should never resubmit */);
        switch (message.type) {
            case ContainerMessageType.FluidDataStoreOp:
            case ContainerMessageType.Attach:
            case ContainerMessageType.Alias:
                // For Operations, call resubmitDataStoreOp which will find the right store
                // and trigger resubmission on it.
                this.channelCollection.reSubmit(message.type, message.contents, localOpMetadata);
                break;
            case ContainerMessageType.IdAllocation: {
                // Allocation ops are never resubmitted/rebased. This is because they require special handling to
                // avoid being submitted out of order. For example, if the pending state manager contained
                // [idOp1, dataOp1, idOp2, dataOp2] and the resubmission of dataOp1 generated idOp3, that would be
                // placed into the outbox in the same batch as idOp1, but before idOp2 is resubmitted.
                // To avoid this, allocation ops are simply never resubmitted. Prior to invoking the pending state
                // manager to replay pending ops, the runtime will always submit a new allocation range that includes
                // all pending IDs. The resubmitted allocation ops are then ignored here.
                break;
            }
            case ContainerMessageType.BlobAttach:
                this.blobManager.reSubmit(opMetadata);
                break;
            case ContainerMessageType.Rejoin:
                this.submit(message);
                break;
            case ContainerMessageType.GC:
                this.submit(message);
                break;
            case ContainerMessageType.DocumentSchemaChange:
                // There is no need to resend this message. Document schema controller will properly resend it again (if needed)
                // on a first occasion (any ops sent after reconnect). There is a good chance, though, that it will not want to
                // send any ops, as some other client already changed schema.
                break;
            default: {
                // This case should be very rare - it would imply an op was stashed from a
                // future version of runtime code and now is being applied on an older version.
                const compatBehavior = message.compatDetails?.behavior;
                if (compatBehaviorAllowsMessageType(message.type, compatBehavior)) {
                    // We do not ultimately resubmit it, to be consistent with this version of the code.
                    this.logger.sendTelemetryEvent({
                        eventName: "resubmitUnrecognizedMessageTypeAllowed",
                        messageDetails: { type: message.type, compatBehavior },
                    });
                }
                else {
                    const error = DataProcessingError.create("Resubmitting runtime message of unexpected type", "reSubmitCore", undefined /* sequencedMessage */, {
                        messageDetails: JSON.stringify({
                            type: message.type,
                            compatBehavior,
                        }),
                    });
                    this.closeFn(error);
                    throw error;
                }
            }
        }
    }
    rollback(content, localOpMetadata) {
        // Need to parse from string for back-compat
        const { type, contents } = this.parseLocalOpContent(content);
        switch (type) {
            case ContainerMessageType.FluidDataStoreOp:
                // For operations, call rollbackDataStoreOp which will find the right store
                // and trigger rollback on it.
                this.channelCollection.rollback(type, contents, localOpMetadata);
                break;
            default:
                // Don't check message.compatDetails because this is for rolling back a local op so the type will be known
                throw new Error(`Can't rollback ${type}`);
        }
    }
    /** Implementation of ISummarizerInternalsProvider.refreshLatestSummaryAck */
    async refreshLatestSummaryAck(options) {
        const { proposalHandle, ackHandle, summaryRefSeq, summaryLogger } = options;
        // proposalHandle is always passed from RunningSummarizer.
        assert(proposalHandle !== undefined, 0x766 /* proposalHandle should be available */);
        const result = await this.summarizerNode.refreshLatestSummary(proposalHandle, summaryRefSeq);
        /* eslint-disable jsdoc/check-indentation */
        /**
         * If the snapshot corresponding to the ack is not tracked by this client, it was submitted by another client.
         * Take action as per the following scenarios:
         * 1. If that snapshot is older than the one tracked by this client, ignore the ack because only the latest
         *    snapshot is tracked.
         * 2. If that snapshot is newer, attempt to fetch the latest snapshot and do one of the following:
         *    2.1. If the fetched snapshot is same or newer than the one for which ack was received, close this client.
         *         The next summarizer client will likely start from this snapshot and get out of this state. Fetching
         *         the snapshot updates the cache for this client so if it's re-elected as summarizer, this will prevent
         *         any thrashing.
         *    2.2. If the fetched snapshot is older than the one for which ack was received, ignore the ack. This can
         *         happen in scenarios where the snapshot for the ack was lost in storage (in scenarios like DB rollback,
         *         etc.) but the summary ack is still there because it's tracked a different service. In such cases,
         *         ignoring the ack is the correct thing to do because the latest snapshot in storage is not the one for
         *         the ack but is still the one tracked by this client. If we were to close the summarizer like in the
         *         previous scenario, it will result in this document stuck in this state in a loop.
         */
        /* eslint-enable jsdoc/check-indentation */
        if (!result.isSummaryTracked) {
            if (result.isSummaryNewer) {
                await this.fetchLatestSnapshotAndMaybeClose(summaryRefSeq, ackHandle, summaryLogger);
            }
            return;
        }
        // Notify the garbage collector so it can update its latest summary state.
        await this.garbageCollector.refreshLatestSummary(result);
        // If we here, the ack was tracked by this client. Update the summary context of the last ack.
        this.lastAckedSummaryContext = {
            proposalHandle,
            ackHandle,
            referenceSequenceNumber: summaryRefSeq,
        };
    }
    /**
     * Fetches the latest snapshot from storage. If the fetched snapshot is same or newer than the one for which ack
     * was received, close this client. Fetching the snapshot will update the cache for this client so if it's
     * re-elected as summarizer, this will prevent any thrashing.
     * If the fetched snapshot is older than the one for which ack was received, ignore the ack and return. This can
     * happen in scenarios where the snapshot for the ack was lost in storage in scenarios like DB rollback, etc.
     */
    async fetchLatestSnapshotAndMaybeClose(targetRefSeq, targetAckHandle, logger) {
        const fetchedSnapshotRefSeq = await PerformanceEvent.timedExecAsync(logger, { eventName: "RefreshLatestSummaryAckFetch" }, async (perfEvent) => {
            const props = { targetRefSeq, targetAckHandle };
            const trace = Trace.start();
            let snapshotTree;
            const scenarioName = "RefreshLatestSummaryAckFetch";
            // If loader supplied us the ISnapshot when loading, the new getSnapshotApi is supported and feature gate is ON, then use the
            // new API, otherwise it will reduce the service performance because the service will need to recalculate the full snapshot
            // in case previously getSnapshotApi was used and now we use the getVersions API.
            if (this.isSnapshotInstanceOfISnapshot &&
                this.storage.getSnapshot !== undefined &&
                this.mc.config.getBoolean("Fluid.Container.UseLoadingGroupIdForSnapshotFetch2") ===
                    true) {
                const snapshot = await this.storage.getSnapshot({
                    scenarioName,
                    fetchSource: FetchSource.noCache,
                });
                const id = snapshot.snapshotTree.id;
                assert(id !== undefined, 0x9d0 /* id of the fetched snapshot should be defined */);
                props.snapshotVersion = id;
                snapshotTree = snapshot.snapshotTree;
            }
            else {
                const versions = await this.storage.getVersions(null, 1, scenarioName, FetchSource.noCache);
                assert(!!versions && !!versions[0], 0x137 /* "Failed to get version from storage" */);
                snapshotTree = await this.storage.getSnapshotTree(versions[0]);
                assert(!!snapshotTree, 0x138 /* "Failed to get snapshot from storage" */);
                props.snapshotVersion = versions[0].id;
            }
            props.getSnapshotDuration = trace.trace().duration;
            const readAndParseBlob = async (id) => readAndParse(this.storage, id);
            const snapshotRefSeq = await seqFromTree(snapshotTree, readAndParseBlob);
            props.snapshotRefSeq = snapshotRefSeq;
            props.newerSnapshotPresent = snapshotRefSeq >= targetRefSeq;
            perfEvent.end({ details: props });
            return snapshotRefSeq;
        });
        // If the snapshot that was fetched is older than the target snapshot, return. The summarizer will not be closed
        // because the snapshot is likely deleted from storage and it so, closing the summarizer will result in the
        // document being stuck in this state.
        if (fetchedSnapshotRefSeq < targetRefSeq) {
            return;
        }
        await delay(this.closeSummarizerDelayMs);
        this._summarizer?.stop("latestSummaryStateStale");
        this.disposeFn();
    }
    getPendingLocalState(props) {
        this.verifyNotClosed();
        if (this._orderSequentiallyCalls !== 0) {
            throw new UsageError("can't get state during orderSequentially");
        }
        this.imminentClosure || (this.imminentClosure = props?.notifyImminentClosure ?? false);
        const getSyncState = (pendingAttachmentBlobs) => {
            const pending = this.pendingStateManager.getLocalState(props?.snapshotSequenceNumber);
            const sessionExpiryTimerStarted = props?.sessionExpiryTimerStarted ?? this.garbageCollector.sessionExpiryTimerStarted;
            const pendingIdCompressorState = this._idCompressor?.serialize(true);
            return {
                pending,
                pendingIdCompressorState,
                pendingAttachmentBlobs,
                sessionExpiryTimerStarted,
            };
        };
        const perfEvent = {
            eventName: "getPendingLocalState",
            notifyImminentClosure: props?.notifyImminentClosure,
        };
        const logAndReturnPendingState = (event, pendingState) => {
            event.end({
                attachmentBlobsSize: Object.keys(pendingState?.pendingAttachmentBlobs ?? {}).length,
                pendingOpsSize: pendingState?.pending?.pendingStates.length,
            });
            return pendingState;
        };
        // Flush pending batch.
        // getPendingLocalState() is only exposed through Container.closeAndGetPendingLocalState(), so it's safe
        // to close current batch.
        this.flush();
        return props?.notifyImminentClosure === true
            ? PerformanceEvent.timedExecAsync(this.mc.logger, perfEvent, async (event) => logAndReturnPendingState(event, getSyncState(await this.blobManager.attachAndGetPendingBlobs(props?.stopBlobAttachingSignal))))
            : PerformanceEvent.timedExec(this.mc.logger, perfEvent, (event) => logAndReturnPendingState(event, getSyncState()));
    }
    summarizeOnDemand(options) {
        if (this.isSummarizerClient) {
            return this.summarizer.summarizeOnDemand(options);
        }
        else if (this.summaryManager !== undefined) {
            return this.summaryManager.summarizeOnDemand(options);
        }
        else {
            // If we're not the summarizer, and we don't have a summaryManager, we expect that
            // disableSummaries is turned on. We are throwing instead of returning a failure here,
            // because it is a misuse of the API rather than an expected failure.
            throw new UsageError(`Can't summarize, disableSummaries: ${this.summariesDisabled}`);
        }
    }
    enqueueSummarize(options) {
        if (this.isSummarizerClient) {
            return this.summarizer.enqueueSummarize(options);
        }
        else if (this.summaryManager !== undefined) {
            return this.summaryManager.enqueueSummarize(options);
        }
        else {
            // If we're not the summarizer, and we don't have a summaryManager, we expect that
            // generateSummaries is turned off. We are throwing instead of returning a failure here,
            // because it is a misuse of the API rather than an expected failure.
            throw new UsageError(`Can't summarize, disableSummaries: ${this.summariesDisabled}`);
        }
    }
    /**
     * Forms a function that will create and retrieve a Summarizer.
     */
    formCreateSummarizerFn(loader) {
        return async () => {
            return createSummarizer(loader, `/${summarizerRequestUrl}`);
        };
    }
    validateSummaryHeuristicConfiguration(configuration) {
        // eslint-disable-next-line no-restricted-syntax
        for (const prop in configuration) {
            if (typeof configuration[prop] === "number" && configuration[prop] < 0) {
                throw new UsageError(`Summary heuristic configuration property "${prop}" cannot be less than 0`);
            }
        }
        if (configuration.minIdleTime > configuration.maxIdleTime) {
            throw new UsageError(`"minIdleTime" [${configuration.minIdleTime}] cannot be greater than "maxIdleTime" [${configuration.maxIdleTime}]`);
        }
    }
    get groupedBatchingEnabled() {
        return this.sessionSchema.opGroupingEnabled === true;
    }
}
//# sourceMappingURL=containerRuntime.js.map